import { CargoCoolingRange, CargoType, CargoTypeSub, ServiceType } from '@prisma/client';

import { AirCanada } from '../../../api/src/lib/integrations/ac/typings';
import { Emirates } from '../../../api/src/lib/integrations/ek/typings';

export type AirlineProductToPeli<T> = {
  name: string;
  code: T;
  cargoType: CargoType;
  cargoTypeSub?: CargoTypeSub | null;
  serviceType: ServiceType;

  visibleTo?: {
    cargoTypes: (CargoType | null | undefined)[];
    cargoTypeSubs?: (CargoTypeSub | null | undefined)[];
    serviceTypes?: ServiceType[];
    cargoCoolingRanges?: CargoCoolingRange[];
    unNumbers?: string[];
  };
};

export type AirlineProductToPeliMap<T extends string> = Record<T, AirlineProductToPeli<T>>;

export const EK_PRODUCT_CODE_PELI: AirlineProductToPeliMap<Emirates.ProductCode> = {
  [Emirates.ProductCode.GCR]: {
    name: 'Emirates AirFreight',
    code: Emirates.ProductCode.GCR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.AXA]: {
    name: 'Emirates AirFreight Priority',
    code: Emirates.ProductCode.AXA,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [null, undefined, CargoType.GeneralCargo, CargoType.DangerousGoods, CargoType.Perishable],
      cargoTypeSubs: [null, undefined, CargoTypeSub.GENERAL],
    },
  },
  [Emirates.ProductCode.PXS]: {
    name: 'Emirates Fresh',
    code: Emirates.ProductCode.PXS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PXD]: {
    name: 'Emirates Fresh Breathe',
    code: Emirates.ProductCode.PXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PERISHABLE, CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.PNF]: {
    name: 'Emirates Fresh Perishable Non Food',
    code: Emirates.ProductCode.PNF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.GENERAL,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.PXF]: {
    name: 'Emirates Fresh Perishable Frozen',
    code: Emirates.ProductCode.PXF,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXM]: {
    name: 'Emirates Medical Devices',
    code: Emirates.ProductCode.AXM,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AXS]: {
    name: 'Emirates Pharma',
    code: Emirates.ProductCode.AXS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.General,

    visibleTo: {
      cargoTypes: [CargoType.Perishable, CargoType.Pharma],
      cargoTypeSubs: [CargoTypeSub.PHARMA],
    },
  },
  [Emirates.ProductCode.AXD]: {
    name: 'Emirates Pharma Plus',
    code: Emirates.ProductCode.AXD,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    serviceType: ServiceType.Expedited,
  },
  [Emirates.ProductCode.AXT]: {
    name: 'Emirates Vital',
    code: Emirates.ProductCode.AXT,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VAL]: {
    name: 'Emirates Secure VAL',
    code: Emirates.ProductCode.VAL,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.VUN]: {
    name: 'Emirates Secure VUN',
    code: Emirates.ProductCode.VUN,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.AOG]: {
    name: 'Emirates AOG',
    code: Emirates.ProductCode.AOG,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Urgent,
  },
  [Emirates.ProductCode.PAX]: {
    name: 'Emirates DG-PAX',
    code: Emirates.ProductCode.PAX,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [Emirates.ProductCode.CAO]: {
    name: 'Emirates DG-CAO',
    code: Emirates.ProductCode.CAO,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
};

export const AC_PRODUCT_CODE_PELI: AirlineProductToPeliMap<AirCanada.ProductCode> = {
  [AirCanada.ProductCode.AFR]: {
    name: 'AC General - Standard',
    code: AirCanada.ProductCode.AFR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPR]: {
    name: 'AC General - Priority1',
    code: AirCanada.ProductCode.PPR,
    cargoType: CargoType.GeneralCargo,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.ADG]: {
    name: 'AC DGR - Standard',
    code: AirCanada.ProductCode.ADG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PDG]: {
    name: 'AC DGR - Priority1',
    code: AirCanada.ProductCode.PDG,
    cargoType: CargoType.DangerousGoods,
    cargoTypeSub: null,
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.PPS]: {
    name: 'AC Pharmacair Simple',
    code: AirCanada.ProductCode.PPS,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PPP]: {
    name: 'AC Pharmacair Plus',
    code: AirCanada.ProductCode.PPP,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PHARMA,
    // cargoTypeSub: CargoTypeSub.GENERAL, // ALSO
    serviceType: ServiceType.Expedited,
  },
  [AirCanada.ProductCode.AFH]: {
    name: 'AC Fresh - Standard',
    code: AirCanada.ProductCode.AFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.General,
  },
  [AirCanada.ProductCode.PFH]: {
    name: 'AC Fresh - Priority',
    code: AirCanada.ProductCode.PFH,
    cargoType: CargoType.Perishable,
    cargoTypeSub: CargoTypeSub.PERISHABLE,
    serviceType: ServiceType.Expedited,
  },
};

export type CommodityMapEK = {
  commodityCode: string;
  product: string;

  productSHC?: string;
  commoditySHC?: string;
};

export type CommodityItem = {
  value: string;
  label: string;
  isGeneric?: boolean;
  sortPosition?: number;

  EK?: CommodityMapEK[];
};

export const getPrettyCommodity = (value: string) => {
  return findCommodity(value)?.label ?? value;
};

export const findCommodity = (value: string) => {
  return COMMODITIES?.find((n) => n.value === value);
};

export const getCommoditiesFor = (
  cargoType: CargoType,
  { excludeGeneric, cargoTypeSub }: { excludeGeneric?: boolean; cargoTypeSub?: CargoTypeSub } = {
    excludeGeneric: false,
  },
) => {
  // We are using only EK as the base right now since they are the most restrictive.
  const products = Object.values(EK_PRODUCT_CODE_PELI)
    ?.filter((node) => {
      const matchesCargoType = node?.visibleTo?.cargoTypes?.includes(cargoType);
      const matchesCargoTypeSub = cargoTypeSub ? node?.visibleTo?.cargoTypeSubs?.includes(cargoTypeSub) : true;

      return matchesCargoType && matchesCargoTypeSub;
    })
    ?.map((node) => node.code as Emirates.ProductCode);

  if (!products.length) return COMMODITIES;

  const filtered = COMMODITIES?.filter((node) => {
    if (excludeGeneric && node?.isGeneric) return false;
    return node.EK?.some((item) => products.includes(item.product as Emirates.ProductCode));
  });

  return filtered?.sort((a, b) => {
    const aHasSort = a.sortPosition !== undefined;
    const bHasSort = b.sortPosition !== undefined;

    // Both have sort position, sort by it
    if (aHasSort && bHasSort) return a.sortPosition! - b.sortPosition!;

    // One has sort position, put it first
    if (aHasSort) return -1;
    if (bHasSort) return 1;

    // Neither has sort position, sort by label
    return a.label.localeCompare(b.label);
  });
};

export const COMMODITIES: CommodityItem[] = [
  {
    value: 'HUMAN HAIR',
    label: 'Human Hair',
    EK: [
      { commodityCode: '9090', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9090', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HUMAN REMAINS, UNCREMATED',
    label: 'Human Remains, Uncremated',
    EK: [
      { commodityCode: '9091', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9091', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CREMATED HUMAN REMAINS, ASHES',
    label: 'Cremated Human Remains, Ashes',
    EK: [{ commodityCode: '9095', product: 'GCR', productSHC: '', commoditySHC: '' }],
  },
  {
    value: 'GIFT PARCELS  CONTAINING FOOD, CLOTHING ',
    label: 'Gift Parcels Containing Food, Clothing',
    EK: [
      { commodityCode: '9102', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9102', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GIFT PARCELS  CONT FOOD,CLOTHG,SHOES,MEDI,COSMETCS,TOILET AR',
    label: 'Gift Parcels Containing Food, Clothing, Shoes, Medicines, Cosmetics, Toilet Articles',
    EK: [
      { commodityCode: '9104', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9104', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GIFT SHIPMENTS  CONTAINING FOOD,MEDICAL SUPPLIES ',
    label: 'Gift Shipments Containing Food, Medical Supplies',
    EK: [
      { commodityCode: '9105', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9105', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFFS,BEVERAGES FR CONSUM/HOUSEHOLD USE-NOT FOR RESALE ',
    label: 'Foodstuffs, Beverages for Consumer or Household Use - Not for Resale',
    EK: [
      { commodityCode: '9106', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9106', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ATHLETIC EQUIPMENT,SPORTING GOODS',
    label: 'Athletic Equipment, Sporting Goods',
    EK: [
      { commodityCode: '9200', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOYS,GAMES,ATHLETIC GOODS,SPORTING GOODS',
    label: 'Toys, games, athletic goods, sporting goods',
    EK: [
      { commodityCode: '9202', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9202', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOODEN TOYS',
    label: 'Wooden Toys',
    EK: [
      { commodityCode: '9204', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9204', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOYS,GAMES,ATHLETIC GOODS,SPORTG GOODS EXCL WEARING APPAREL',
    label: 'Toys, Games, Athletic Goods, Sporting Goods excluding Wearing Apparel',
    EK: [
      { commodityCode: '9205', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9205', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOYS,GAMES,ATHELTIC GOODS INCL SPORTG/HUNTG GUNS EXCL WATCHE',
    label: 'Toys, Games, Athletic Goods, including Sporting or Hunting Guns, excluding Watches',
    EK: [
      { commodityCode: '9206', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOYS GAMES-INCL PACKAGING MTRL WHEN HPD WITH MAIN ARTCL',
    label: 'Toys, games, including packaging material when shipped with main article.',
    EK: [
      { commodityCode: '9207', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9207', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SADDLES, RIDING EQPT',
    label: 'Saddles, Riding Equipment',
    EK: [
      { commodityCode: '9208', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9208', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BASEBALLS',
    label: 'Baseballs',
    EK: [
      { commodityCode: '9213', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9213', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SCALE MODELS  SHIPS ',
    label: 'Scale Models of Ships',
    EK: [
      { commodityCode: '9215', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9215', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SKATEBOARD  INCL HELMETS, PROTECTIVE PADS ',
    label: 'Skateboard Including Helmets, Protective Pads',
    EK: [
      { commodityCode: '9229', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9229', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FISHING EQUIPMENT  INCLUDING ACCESSORIES ',
    label: 'Fishing Equipment, including Accessories',
    EK: [
      { commodityCode: '9241', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9241', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SKIS,SKIPOLES,SKI BINDINGS',
    label: 'Skis, Ski Poles, Ski Bindings',
    EK: [
      { commodityCode: '9274', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9274', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GUNS, FIREARMS, AMMUNITION',
    label: 'Guns, Firearms, Ammunition',
    EK: [
      { commodityCode: '9300', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9300', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AMMUNITION FOR SPORTING GUNS',
    label: 'Ammunition for Sporting Guns',
    EK: [
      { commodityCode: '9311', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9311', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OFFICE SUPPLIES  EXCLUDING MACHINERY ',
    label: 'Office Supplies, excluding Machinery',
    EK: [
      { commodityCode: '9400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PENS,PENCILS',
    label: 'Pens, Pencils',
    EK: [
      { commodityCode: '9470', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9470', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PENS, PENCILS  INCL STANDS, HOLDERS, CASES ',
    label: 'Pens, pencils, including stands, holders, and cases.',
    EK: [
      { commodityCode: '9471', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9471', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PENS, PENCILS  INCL DISPLAY MATERIAL ',
    label: 'Pens, Pencils, Including Display Material',
    EK: [
      { commodityCode: '9473', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9473', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAINTINGS, ETCHINGS,ENGRAVINGS',
    label: 'Paintings, Etchings, Engravings',
    EK: [
      { commodityCode: '9500', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9500', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PRODS MADE OF TEXTLS,METAL,WOOD,CORK,STRAW,WICKER',
    label: 'Handicraft Products Made of Textiles, Metal, Wood, Cork, Straw, Wicker',
    EK: [
      { commodityCode: '9503', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9503', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SOUVENIRS INCL LEATHER GOODS,SILVERWARE,WOOD/COPPER MFRS,BON',
    label: 'Souvenirs including leather goods, silverware, wood/copper manufactures, and bone',
    EK: [
      { commodityCode: '9505', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9505', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CURIOS CARVED/WOODEN ,BONE ARTICLES,HORN ARTICLES',
    label: 'Curios Carved from Wooden, Bone Articles, or Horn Articles',
    EK: [
      { commodityCode: '9506', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9506', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SOUVENIRS LEATHER/WOOD/COPPER/IVORY/BONE/HORN GDS. ',
    label: 'Souvenirs Leather, Wood, Copper, Ivory, Bone, Horn Goods.',
    EK: [
      { commodityCode: '9508', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9508', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,CLAY,HANDLOOM TXT',
    label: 'Handicrafts made of brass, copper, iron, wood, clay, handloom textiles.',
    EK: [
      { commodityCode: '9509', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9509', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CURIOS CARVED/WOODEN ,BONE/HORN ARTCLS EXCL IVORY ARTCLS',
    label: 'Curios, carved or wooden, bone or horn articles, excluding ivory articles.',
    EK: [
      { commodityCode: '9511', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9511', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,STONE,BAMBOO,WICK',
    label: 'Handicrafts Made of Brass, Copper, Iron, Wood, Stone, Bamboo, Wicker',
    EK: [
      { commodityCode: '9512', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9512', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PDCTS TXTLS,METAL,WOOD,CORK,STRAW,WICKER,LEATHER,',
    label: 'Handicraft products made of textiles, metal, wood, cork, straw, wicker',
    EK: [
      { commodityCode: '9513', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9513', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS MADE OF BRASS,COPPER,IRON,WOOD,BAMBOO,WICKER,PAP',
    label: 'Handicrafts made of brass, copper, iron, wood, bamboo, wicker, paper',
    EK: [
      { commodityCode: '9515', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9515', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PRODUCTS MADE OF TEXTILES,METAL,WOOD,STRAW,LEATHE',
    label: 'Handicraft products made of textiles, metal, wood, straw, leather',
    EK: [
      { commodityCode: '9516', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9516', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PRODS MADE OF METAL,WOOD,STRAW,LEATHER,EARTH,ONYX',
    label: 'Handicraft Products Made of Metal, Wood, Straw, Leather, Earth, Onyx',
    EK: [
      { commodityCode: '9517', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9517', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS MADE OF SISAL,FIBRE,BANANA FIBRE,BAMBOO,LEATHER,',
    label: 'Handicrafts Made of Sisal, Fiber, Banana Fiber, Bamboo, Leather',
    EK: [
      { commodityCode: '9520', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9520', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PRODS TXTLS,METAL,WOOD,CLAY EXCL WEARG AAPAREL',
    label: 'Handicraft Products Textiles, Metal, Wood, Clay excluding Wearing Apparel',
    EK: [
      { commodityCode: '9522', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9522', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS METAL,WOOD,CORK,STRAW EXCL TXTL,LEATHER,DIAMONDS',
    label: 'Handicrafts made of metal, wood, cork, straw, excluding textiles and leather.',
    EK: [
      { commodityCode: '9524', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9524', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS(MADE OF METAL,WOOD,CORK,STRAW,BAMBOO,WICKER,CLAY',
    label: 'Handicrafts made of metal, wood, cork, straw, bamboo, wicker',
    EK: [
      { commodityCode: '9525', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9525', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BUTTONS, BUCKLES',
    label: 'Buttons, Buckles',
    EK: [
      { commodityCode: '9526', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9526', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFTS',
    label: 'Handicrafts',
    EK: [
      { commodityCode: '9527', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9527', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FASTENERS,ZIPPERS,BUCKLES,HANDLES,HANDBAG FRAMES',
    label: 'Fasteners, Zippers, Buckles, Handles, Handbags',
    EK: [
      { commodityCode: '9529', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9529', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAKE DECORATIONS  MADE OF SUGAR,PLASTIC ',
    label: 'Cake Decorations Made of Sugar, Plastic',
    EK: [
      { commodityCode: '9533', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9533', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BRUSHES',
    label: 'Brushes',
    EK: [
      { commodityCode: '9540', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9540', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDBAGS',
    label: 'Handbags',
    EK: [
      { commodityCode: '9552', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9552', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PURSES,HANDBAGS,WALLETS,BRIEFCASES,SCHOOL BAGS-OF LEATHER',
    label: 'Purses, Handbags, Wallets, Briefcases, School Bags',
    EK: [
      { commodityCode: '9553', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9553', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDBAGS,PURSES,WALLETS INCL ACCESSORIES OTHER THAN LEATHER',
    label: 'Handbags, Purses, Wallets including Accessories Other Than Leather',
    EK: [
      { commodityCode: '9557', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9557', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SUITCASES,OVERNIGHT BAGS,BRIEFCASES,ATTACHE CASES,HANDBAGS,W',
    label: 'Suitcases, Overnight Bags, Briefcases, Attache Cases, Handbags',
    EK: [
      { commodityCode: '9558', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9558', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RELIGIOUS ARTICLES EXCL.PRINTED MATTER PICTURES,VESTMENTS',
    label: 'Religious Articles excluding Printed Matter Pictures, Vestments',
    EK: [
      { commodityCode: '9560', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9560', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADVTG ARTCLES, DISPLAY EQPT - NOT FOR SALE',
    label: 'Advertising Articles, Display Equipment - Not for Sale',
    EK: [
      { commodityCode: '9576', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9576', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOBACCO, CIGARS, CIGARETTES',
    label: 'Tobacco, Cigars, Cigarettes',
    EK: [
      { commodityCode: '9601', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9601', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOBACCO,CIGARS,CIGARETTES,PIPES,HUMIDORS,CIGAR/CIGARETTE HOL',
    label: 'Tobacco, Cigars, Cigarettes, Pipes, Humidors, Cigar/Cigarette Holders',
    EK: [
      { commodityCode: '9602', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9602', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INDIAN BEEDIES',
    label: 'Indian Beedies',
    EK: [
      { commodityCode: '9613', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9613', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PIPES  SMOKING ',
    label: 'Pipes, Smoking',
    EK: [
      { commodityCode: '9615', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9615', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ACCESSORIES',
    label: 'Accessories',
    EK: [
      { commodityCode: '9623', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9623', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LIGHTERS',
    label: 'Lighters',
    EK: [
      { commodityCode: '9625', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9625', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FEEDERS,WATERERS,EGG TRAYS,EGG GRADERS,CHICKEN CRATES,PARTS',
    label: 'Feeders, Waterers, Egg Trays, Egg Graders, Chicken Crates, Parts',
    EK: [
      { commodityCode: '9660', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9660', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, YARN, THREAD, FIBRES, TEXTILES, WEARING APPAREL, LEATHER, LEATHER MANUFACTURES',
    label: 'Garments, yarn, thread, fibers, textiles, wearing apparel, leather, leather manufactures',
    EK: [
      { commodityCode: '9703', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9703', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFACE VEHICLS INCL ACCES MACHNS EXCL STEAM/MOTORSHP MACHN',
    label: 'Surface Vehicles, Including Accessories and Machines, Excluding Steam or Motor Ship Machines',
    EK: [
      { commodityCode: '9704', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9704', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DATA PROCESSG EQPT,OFC MACHNRY INCL ACCESSORIES,SUPPLIES',
    label: 'Data Processing Equipment, Office Machinery, including Accessories, Supplies',
    EK: [
      { commodityCode: '9705', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9705', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC EQPT/APPL.,TAPES,CASSETTES,PHONOGRAPH RECORDS,WIRE',
    label: 'Electrical Equipment/Appliances, Tapes, Cassettes, Phonograph Records, Wire',
    EK: [
      { commodityCode: '9706', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9706', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOOLS INCLUDING RAZOR BLADES , CUTLERY',
    label: 'Tools, including razor blades, and cutlery.',
    EK: [
      { commodityCode: '9707', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9707', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC INSTRUMENTS, PROJECTIONINSTRUMENTS - INCLUDING APPARATUS, SUPPL',
    label: 'Cameras, Optical Goods, Photographic Instruments, Projection Instruments - Including Apparatus, Supplies',
    EK: [
      { commodityCode: '9708', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8401', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8401', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9708', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEM,CHEM PRODS,DRUGS,COSMETCS,PHARMA,ESSENTIAL OILS,PLASTIC',
    label: 'Chemicals, Chemical Products, Drugs, Cosmetics, Pharmaceuticals, Essential Oils',
    EK: [
      { commodityCode: '9709', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9709', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER,PAPER-INCLUDING PHOTOGRAPHIC/SENSITIZED PAPER',
    label: 'Printed Matter, Paper, Including Photographic or Sensitized Paper',
    EK: [
      { commodityCode: '9710', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9710', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOYS,GAMES,SPORTING EQUIPMENT',
    label: 'Toys, Games, Sporting Equipment',
    EK: [
      { commodityCode: '9712', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9712', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MUSICAL INSTRUMENTS',
    label: 'Musical Instruments',
    EK: [
      { commodityCode: '9713', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9713', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CERAMICS,POTTERY,CHINA/EARTHEN/STONE/GLASS/CRYSTALWARE,PORCE ',
    label: 'Ceramics, Pottery, China, Earthenware, Stoneware, Glassware, Crystalware, Porcelain',
    EK: [
      { commodityCode: '9714', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9714', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFACE VEHICLES INCL ACCESSORIES ,MACHINERY,TOOLS,CUTLERY',
    label: 'Surface vehicles, including accessories, machinery, tools, and cutlery.',
    EK: [
      { commodityCode: '9715', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9715', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PERSONAL EFF./HOUSEHOLD GDS. -NOT FOR RESALE ',
    label: 'Personal Effects / Household Goods - Not for Resale',
    EK: [
      { commodityCode: '9716', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9716', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT ENGINES,AIRCRAFT,PARTS EXCL FUSELAGES,WINGS,A/CRAFT',
    label: 'Aircraft Engines, Aircraft Parts Excluding Fuselages, Wings, Aircraft',
    EK: [
      { commodityCode: '9717', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9717', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT PRODS MADE OF TEXTILES,METAL,LEATHER EXCL DIAMOND',
    label: 'Handicraft products made of textiles, metal, leather, excluding diamonds',
    EK: [
      { commodityCode: '9718', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9718', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LIVE AQUATIC PLANTS, FISH (INEDIBLE), CORAL, WEED, FISH FOOD',
    label: 'Live Aquatic Plants, Inedible Fish, Coral, Weed, Fish Food',
    EK: [
      { commodityCode: '9720', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9720', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPER, PAPER PRODUCTS',
    label: 'Paper, Paper Products',
    EK: [
      { commodityCode: '9721', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9721', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'METALS,MINERALS-EXCL GOLD,SILVER,PLATINUM,ALLOYS',
    label: 'Metals, Minerals - Excluding Gold, Silver, Platinum, Alloys',
    EK: [
      { commodityCode: '9722', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9722', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC ARTICLES,WOOD,WOOD PRODUCTS',
    label: 'Plastic Articles, Wood, Wood Products',
    EK: [
      { commodityCode: '9723', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9723', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT PARTS  INCL ACCESSORIES ',
    label: 'Aircraft Parts, Including Accessories',
    EK: [
      { commodityCode: '9724', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9724', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFACE VEHICLES(INCL ACCESSORIES) EXCH STEAM/MOTORSHP PARTS',
    label: 'Surface Vehicles (including Accessories) Excluding Steam or Motor Ship Parts',
    EK: [
      { commodityCode: '9725', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9727', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9725', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9727', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DATA PROCESSG EQPT,OFC MACHNRY-INCL ACCESSORIES,SUPPLIES-ELE',
    label: 'Data Processing Equipment, Office Machinery - Including Accessories and Supplies',
    EK: [
      { commodityCode: '9726', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9726', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER,PAPER-INCL PHOTO SENSITIZED PAPER EXCL NEWSPAPER',
    label: 'Printed Matter, Paper, Including Photo-Sensitized Paper, Excluding Newspapers',
    EK: [
      { commodityCode: '9728', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9728', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFFS,CHEMICALS,ELEC EQPT,TOILET ARTICLES,PLASTIC,PAPER',
    label: 'Foodstuffs, Chemicals, Electrical Equipment, Toilet Articles, Plastic, Paper',
    EK: [
      { commodityCode: '9732', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9732', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TEXTILES,APPAREL,MACHINERY,CUTLER,ELEC EQPT,SURFACE VEHICLES',
    label: 'Textiles, Apparel, Machinery, Cutlery, Electrical Equipment, Surface Vehicles',
    EK: [
      { commodityCode: '9733', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9733', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, YARN, THREAD, FIBRES, TEXTILES, WEARING APPAREL, LEATHER, LEATHER MANUFACTURES EXCLUDING F',
    label: 'Garments, yarn, thread, fibers, textiles, wearing apparel, leather, leather manufactures, excluding fur.',
    EK: [
      { commodityCode: '9734', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9734', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MISCELLANEOUS ITEMS',
    label: 'Miscellaneous Items',
    isGeneric: true,
    EK: [
      { commodityCode: '9735', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9750', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9799', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9750', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
      { commodityCode: '9735', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9750', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9799', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEM,CHEM PRODS,DRUGS,COSMETICS,PARMA,OIL EXCL PLASTIC/TOILE',
    label: 'Chemicals, Chemical Products, Drugs, Cosmetics, Pharma, Oil, excluding Plastic',
    EK: [
      { commodityCode: '9736', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9736', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER',
    label: 'Printed Matter',
    EK: [
      { commodityCode: '9737', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9737', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER  EXCLUDING NEWSPAPERS ',
    label: 'Printed matter, excluding newspapers',
    EK: [
      { commodityCode: '9738', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9738', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PIGS',
    label: 'Pigs',
    EK: [
      { commodityCode: '9741', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1074', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9741', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'IMITATION JEWELRY,FANCY NOVELTIES EXCL CLOTH,WATCHES WHICH C',
    label: 'Imitation Jewelry, Fancy Novelties, Excluding Clothing and Watches Which Contain',
    EK: [
      { commodityCode: '9742', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9742', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CONSTRUCTION EQPT, BUILDING MATERIAL',
    label: 'Construction Equipment, Building Material',
    EK: [
      { commodityCode: '9743', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9743', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'STATIONERY/OFFICE SUPPLIES',
    label: 'Buttons, Buckles',
    EK: [
      { commodityCode: '9744', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9744', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HAIR,WIGS,RADIOS,TVS  MISCELLANEOUS ITEMS..... ',
    label: 'Hair, Wigs, Radios, Televisions, Miscellaneous Items.',
    EK: [
      { commodityCode: '9751', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9751', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TEXTILE FIBRESANDMFRS,METALANDMFRS,NON-METLC  MISC.ITEMS....... ',
    label: 'Textile Fibers and Manufactures, Metal and Manufactures, Non-Metallic Miscellaneous Items.',
    EK: [
      { commodityCode: '9752', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9752', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADHESIVES,SMALL ARMS,ETCHINGS  MISCELLANEOUS ITEMS ',
    label: 'Adhesives, Small Arms, Etchings, Miscellaneous Items',
    EK: [
      { commodityCode: '9796', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9796', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MISCELLANEOUS ITEMS .......',
    label: 'Miscellaneous Items',
    isGeneric: true,
    EK: [
      { commodityCode: '9797', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9798', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9797', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9798', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FURNITURE',
    label: 'Furniture',
    EK: [
      { commodityCode: '9910', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9910', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FURNITURE  EXCL PAINTINGS/ENGRAVINGS/OBJECTS OF ART ',
    label: 'Furniture, excluding paintings, engravings, or objects of art.',
    EK: [
      { commodityCode: '9911', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9911', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NUCLEAR REACTOR PARTS INCL HEAT EXCHANGRS,CONTROL RODS,VALVE',
    label: 'Nuclear Reactor Parts, including Heat Exchangers, Control Rods, and Valves',
    EK: [
      { commodityCode: '9930', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9930', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NUCLEAR REACTOR FUEL CONTAINERS,FUEL RAW MTRL CNTRS EMPTY',
    label: 'Nuclear Reactor Fuel Containers, Fuel Raw Material Containers Empty',
    EK: [
      { commodityCode: '9935', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9935', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GOVERNMENT OWNED STORES  INCLUDING SUPPLIES ',
    label: 'Government Owned Stores, Including Supplies',
    EK: [
      { commodityCode: '9942', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9942', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DIPLOMATIC CARGO, DIPLOMATIC MAIL',
    label: 'Diplomatic Cargo, Diplomatic Mail',
    EK: [
      { commodityCode: '9945', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9945', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'US DIPLOMATIC POUCHES, MOVING ON US GOVT. B/L',
    label: 'US Diplomatic Pouches, Moving on US Government Bill of Lading',
    EK: [
      { commodityCode: '9947', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9947', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHPMTS OF ICI, ITS SUBSIDIARIES & ASSOCIATE COS.',
    label: 'Shipments of ICI, its subsidiaries & associate companies.',
    EK: [
      { commodityCode: '9960', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9960', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOSP SUPPLIES CLOTHG,DRESSG,MEDICINE,LAB TOOLS,PHARMA,MEDICA',
    label: 'Hospital Supplies, Clothing, Dressings, Medicines, Lab Tools, Pharmaceuticals',
    EK: [
      { commodityCode: '9971', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9971', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '***OIL RESEARCHANDDRILLING EQPT, SUPPLIES AND ACCESSORIES',
    label: 'Oil Research and Drilling Equipment, Supplies, and Accessories',
    EK: [
      { commodityCode: '9972', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9972', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BUILDING MATERIAL/TOOLS/FIXTURES/FITTINGS',
    label: 'Building Material, Tools, Fixtures, Fittings',
    EK: [
      { commodityCode: '9973', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9973', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EXPEDITION MATERIAL',
    label: 'Expedition Material',
    EK: [
      { commodityCode: '9974', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9974', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GOODS CONSIGNED TO AMBASSADORS,LEGATIONS,CONSULS OR OTHER OF',
    label: 'Goods Consigned to Ambassadors, Legations, Consuls, or Other Officials',
    EK: [
      { commodityCode: '9981', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9981', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD FURN,KITCHEN EQPT USED  EXCL PER.EFFECTS',
    label: 'Household Furniture, Kitchen Equipment Used, Excluding Personal Effects',
    EK: [
      { commodityCode: '9984', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9984', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'UNACCOMPANIED BAGGAGE, PERSONAL EFFECTS  NOT FORE RESALE ',
    label: 'Unaccompanied Baggage, Personal Effects Not For Resale',
    EK: [
      { commodityCode: '9986', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9986', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLANTS-FLWS,BLBS,SDS,FLORAL/NURSERY STKS,DPLY MATS EXPO USE',
    label: 'Plants, flowers, bulbs, seeds, floral and nursery stocks, display materials for exposition use.',
    EK: [
      { commodityCode: '9989', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9989', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BLDG MTRL,DISPLAY MTRL-FOR EXHIBITION/TRADE FAIRS',
    label: 'Building Material, Display Material for Exhibition/Trade Fairs',
    EK: [
      { commodityCode: '9991', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9991', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD/WEARING GD,COSMETICS,TOILETRIES(USED,NO RESALE)',
    label: 'Household/Wearing Goods, Cosmetics, Toiletries (Used, etc.)',
    EK: [
      { commodityCode: '9993', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9993', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD GOODS USED ,WEARING APPAREL/COSMETICS  USED ',
    label: 'Household Goods Used, Wearing Apparel/Cosmetics Used',
    EK: [
      { commodityCode: '9994', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9994', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WEARING APPAREL,COSEMTICS,TOILET ARTCLS USED NOT FOR RESALE',
    label: 'Wearing Apparel, Cosmetics, Toilet Articles Used Not for Resale',
    EK: [
      { commodityCode: '9995', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9995', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MISCELLANEOUS ITEMS........',
    label: 'Handicrafts made of metal, wood, cork, straw, excluding textiles, leather, and diamonds.',
    EK: [
      { commodityCode: '9996', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9999', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9996', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '9999', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD GOODS, PERSONAL EFFECTS',
    label: 'Household Goods, Personal Effects',
    sortPosition: 12,
    EK: [
      { commodityCode: '9998', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9998', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GENERAL GOODS',
    label: 'General Goods',
    isGeneric: true,
    sortPosition: 1,
    EK: [
      { commodityCode: 'E0000', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0000', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHIP SPARES',
    label: 'Ship Spares',
    sortPosition: 5,
    EK: [
      { commodityCode: 'E0062', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0062', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DANGEROUS GOODS',
    label: 'Dangerous Goods',
    EK: [
      { commodityCode: 'E0063', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0063', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CONSOLIDATION',
    label: 'Consolidation',
    sortPosition: 7,
    isGeneric: true,
    EK: [
      { commodityCode: 'E0068', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0068', product: 'AXS', productSHC: 'PIL', commoditySHC: '' },
      { commodityCode: 'E0068', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0068', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0068', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
      { commodityCode: 'E0068', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SILVER/SILVER PRODUCTS/SILVER JEWELLERY',
    label: 'Silver, Silver Products, Silver Jewelry',
    EK: [
      { commodityCode: 'E0077', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: 'E0077', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PET FOOD',
    label: 'Pet Food',
    sortPosition: 15,
    // sortPosition: 4, for PXS
    EK: [
      { commodityCode: '0902', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '0902', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0902', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'VITAMINS FOR ANIMAL FOOD',
    label: 'Vitamins for Animal Food',
    EK: [
      { commodityCode: '0986', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '0986', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LIVESTOCK',
    label: 'Livestock',
    EK: [{ commodityCode: '1000', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'DEER',
    label: 'Deer',
    EK: [{ commodityCode: '1022', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'LIVE AQUATIC PLANTS, FISH (LIVE, INEDIBLE), CORAL, WEED, FISH FOOD',
    label: 'Live Aquatic Plants, Live Inedible Fish, Coral, Weed, Fish Food',
    EK: [{ commodityCode: '1024', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: '***FISH, LIVE, INEDIBLE',
    label: 'Fish, Live, Inedible',
    EK: [{ commodityCode: '1026', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'HARES',
    label: 'Hares',
    EK: [{ commodityCode: '1040', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SHEEP',
    label: 'Sheep',
    EK: [{ commodityCode: '1052', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'CATTLE',
    label: 'Cattle',
    EK: [{ commodityCode: '1053', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'GOATS',
    label: 'Goats',
    EK: [{ commodityCode: '1054', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'TUBIFEX WORMS',
    label: 'Tubifex worms',
    EK: [{ commodityCode: '1059', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'BEES',
    label: 'Bees',
    EK: [{ commodityCode: '1061', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'TURTLES',
    label: 'Turtles',
    EK: [
      { commodityCode: '1072', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '0385', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0385', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SNAILS',
    label: 'Snails',
    EK: [{ commodityCode: '1091', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'WORMS',
    label: 'Worms',
    EK: [{ commodityCode: '1093', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'FURS, HIDES, SKINS EXCLUDING WEARING APPAREL',
    label: 'Shipments of ICI, its subsidiaries, and associate companies.',
    EK: [
      { commodityCode: '1100', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1100', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HIDES,SKINS- UNDRESSED- FURS EXCL MFRS NUTRIA/REPTILE SKINS',
    label: 'Hides, skins - undressed - furs excluding manufactures of nutria/reptile skins.',
    EK: [
      { commodityCode: '1101', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1101', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FUR PLATES  MADE FROM SEWN SCRAPS ',
    label: 'Fur Plates Made from Sewn Scraps',
    EK: [
      { commodityCode: '1102', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1102', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FURS, FUR WEARING APPAREL, SKINS',
    label: 'Tobacco, Cigars, Cigarettes',
    EK: [
      { commodityCode: '1103', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1103', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LAMB/KID/SHEEP/GOAT SKINS EXCL WEARING APPAREL',
    label: 'Lamb, Kid, Sheep, and Goat Skins, Excluding Wearing Apparel',
    EK: [
      { commodityCode: '1125', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1125', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FUR SCRAPS',
    label: 'Fur Scraps',
    EK: [
      { commodityCode: '1150', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1150', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CATTLE HIDES, SHEEP SKINS, GOAT SKINS',
    label: 'I need the value from the previous column to clean it.',
    EK: [
      { commodityCode: '1164', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1164', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CARACUL, ASTRAKHAN SKINS',
    label: 'Caracul, Astrakhan Skins',
    EK: [
      { commodityCode: '1188', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1188', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'REPTILE SKINS',
    label: 'Reptile Skins',
    EK: [
      { commodityCode: '1190', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1190', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CROCODILE SKINS  WET/SALTED ',
    label: 'Crocodile Skins, Wet/Salted',
    EK: [
      { commodityCode: '1194', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1194', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FURS, HIDES, SKINS EXCLUDING MINK, WEARING APPAREL',
    label: 'Furs, Hides, Skins Excluding Mink, Wearing Apparel',
    EK: [
      { commodityCode: '1195', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1195', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'NUTRIA SKINS',
    label: 'Nutria Skins',
    EK: [
      { commodityCode: '1196', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1196', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER,HIDES AND SKINS - EXCL. FURS AND PELTS',
    label: 'Leather, Hides and Skins - Excluding Furs and Pelts',
    EK: [
      { commodityCode: '1198', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1198', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER, LEATHER GARMENTS',
    label: 'Leather, Leather Garments',
    EK: [
      { commodityCode: '1200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1200', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER, LEATHER PRODUCTS',
    label: 'Leather, leather products',
    EK: [
      { commodityCode: '1201', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1201', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER TANNED,DYED,FINISHED,SEMI-FINISHED,CUT TO SHAPE ',
    label: 'Shipments of Imperial Chemical Industries, its Subsidiaries and Associate Companies',
    EK: [
      { commodityCode: '1204', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1204', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER  UNMANUFACTURED ',
    label: 'Leather, Unmanufactured',
    EK: [
      { commodityCode: '1205', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1205', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SHOE LEATHER ALONE OR TOGETHER WITH OTHER SHOE MFG MTRLS ',
    label: 'Shoe Leather Alone or Together with Other Shoe Manufacturing Materials',
    EK: [
      { commodityCode: '1206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1206', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER,HIDES,SKINS -EXCL.WEARING APPAREL,FURS',
    label: 'Leather, Hides, and Skins, excluding Wearing Apparel and Furs',
    EK: [
      { commodityCode: '1207', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '1207', product: 'GCR', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEMICALS, PHARMACEUTICALS',
    label: 'Chemicals, Pharmaceuticals',
    EK: [{ commodityCode: '6000', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'INSULIN, CHEMICALS, DRUGS, PHARMACEUTICALS, MEDICINES',
    label: 'Insulin, Chemicals, Drugs, Pharmaceuticals, Medicines',
    EK: [{ commodityCode: '6001', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'CHEMICAL REAGENTS',
    label: 'Chemical Reagents',
    sortPosition: 5,
    EK: [{ commodityCode: '6010', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'VACCINES',
    label: 'Vaccines',
    EK: [{ commodityCode: '6099', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'BIOLOGICALS, SEMEN SAMPLES, URINE SAMPLES, BACTERIAL CULTURES',
    label: 'Biologicals, Semen Samples, Urine Samples, Bacterial Cultures',
    EK: [{ commodityCode: '6100', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'VITAMINS,ANTIBIOTICS',
    label: 'Vitamins, Antibiotics',
    sortPosition: 2,
    EK: [{ commodityCode: '6103', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'BLOOD SERUM, HAEMODERIVATIVES, BLOODPLASMA',
    label: 'Blood Serum, Hemoderivatives, Blood Plasma',
    EK: [{ commodityCode: '6107', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'BLOODSERUM',
    label: 'Blood Serum',
    EK: [{ commodityCode: '6108', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'HUMAN BLOOD',
    label: 'Human Blood',
    EK: [{ commodityCode: '6109', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'PHARMACEUTICALS',
    label: 'Pharmaceuticals',
    sortPosition: 3,
    EK: [{ commodityCode: '6500', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'MEDICINES, PHARMACEUTICALS',
    label: 'Medicines, Pharmaceuticals',
    sortPosition: 4,
    EK: [{ commodityCode: '6501', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'General Pharma',
    label: 'General Pharma',
    isGeneric: false,
    sortPosition: 1,
    EK: [{ commodityCode: 'E0108', product: 'AXS', productSHC: 'PIL', commoditySHC: '' }],
  },
  {
    value: 'FRUIT  DRY,FRESH ',
    label: 'Fruit, Dry, Fresh',
    EK: [
      { commodityCode: '0002', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0002', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFF EXCL CAVAIR,HATCHING EGGS ,SPICES AND BEVERAGES.',
    label: 'Foodstuff, excluding caviar, hatching eggs, spices, and beverages.',
    EK: [
      { commodityCode: '0003', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0003', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFFS, SPICES, BEVERAGES',
    label: 'Foodstuffs, Spices, Beverages',
    EK: [
      { commodityCode: '0006', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0006', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0006', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUITS, VEGETABLES',
    label: 'Tobacco, Cigars, Cigarettes',
    EK: [
      { commodityCode: '0007', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0007', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '****FRUITS,VEGETABLES-FRESH-',
    label: 'Fruits, Vegetables - Fresh',
    EK: [
      { commodityCode: '0008', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0008', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFFS EXCL STRAWBERRIES ,SPICES,BEVERAGES',
    label: 'Foodstuffs excluding strawberries, spices, and beverages.',
    EK: [
      { commodityCode: '0009', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0009', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'INFANT FOOD',
    label: 'Infant Food',
    EK: [
      { commodityCode: '0010', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0010', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFF,BEVERAGES-DESTINED TO EMBASSY/COUNSULATE/LEGATION',
    label: 'Foodstuffs, Beverages - Destined to Embassy/Consulate/Legation',
    EK: [
      { commodityCode: '0011', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0011', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FOODSTUFF EXCL MEAT,FISH,SEAFOOD SPICES,BEVERAGES',
    label: 'Foodstuff excluding meat, fish, seafood, spices, and beverages.',
    EK: [
      { commodityCode: '0012', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0012', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '****FRUITS AND VEGETABLES EXCL BANANAS',
    label: 'Fruits and Vegetables Excluding Bananas',
    EK: [
      { commodityCode: '0014', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0014', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUITS EXCL STRAWBERRIES VEGETABLES',
    label: 'Fruits excluding Strawberries and Vegetables',
    EK: [
      { commodityCode: '0016', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0016', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TUBERS,EARTH NUTS',
    label: 'Tubers, Earth Nuts',
    EK: [
      { commodityCode: '0017', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0017', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ASPARAGUS,MARACUYA',
    label: 'Asparagus, Maracuya',
    EK: [
      { commodityCode: '0018', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0018', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'NUTS',
    label: 'Nuts',
    EK: [
      { commodityCode: '0020', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0020', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0020', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HOT PEPPERS,EGGPLANTS',
    label: 'Hot Peppers, Eggplants',
    EK: [
      { commodityCode: '0023', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0023', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'AVOCADOS,PINEAPPLES,PAPAYA,MELONS,TAMARIND,CHAYOTE',
    label: 'Avocados, Pineapples, Papaya, Melons, Tamarind',
    EK: [
      { commodityCode: '0024', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0024', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'AVOCADOS,PINEAPPLES',
    label: 'Avocados, Pineapples',
    EK: [
      { commodityCode: '0025', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0025', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MANGOES, MELONS, PINEAPPLES',
    label: 'Mangoes, Melons, Pineapples',
    EK: [
      { commodityCode: '0027', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0027', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MELONS,PERSIMMONS,CITRUS FRUITS',
    label: 'Tobacco, Cigars, Cigarettes',
    EK: [
      { commodityCode: '0029', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0029', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BEANS,CORN,CAPSICUM',
    label: 'Beans, Corn, Capsicum',
    EK: [
      { commodityCode: '0030', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0030', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'KOLA NUTS',
    label: 'Kola Nuts',
    EK: [
      { commodityCode: '0037', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0037', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CANDY',
    label: 'Candy',
    EK: [
      { commodityCode: '0055', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0055', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHOCOLATE MANUFACTURES',
    label: 'Chocolate Manufactures',
    EK: [
      { commodityCode: '0056', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0056', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHOCOLATES MANUFACTURES,CANDIES,WAFERS,BISCUITS',
    label: 'Chocolates Manufactures, Candies, Wafers, Biscuits',
    EK: [
      { commodityCode: '0059', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0059', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CONFECTIONARY,DESSERTS',
    label: 'Advertising Articles, Display Equipment - Not for Sale',
    EK: [
      { commodityCode: '0060', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0060', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEWING-GUM',
    label: 'Chewing Gum',
    EK: [
      { commodityCode: '0070', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0070', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HONEY',
    label: 'Honey',
    EK: [
      { commodityCode: '0075', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0075', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0075', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BEVERAGES',
    label: 'Beverages',
    EK: [
      { commodityCode: '0110', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0110', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MINERAL WATER',
    label: 'Fish, live, inedible',
    EK: [
      { commodityCode: '0113', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0113', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'WINES',
    label: 'Wines',
    EK: [
      { commodityCode: '0130', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0130', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0130', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'COFFEE',
    label: 'Coffee',
    EK: [
      { commodityCode: '0180', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0180', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0180', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'COFFEE ROASTED,GROUND ',
    label: 'Coffee Roasted, Ground',
    EK: [
      { commodityCode: '0183', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0183', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TEA',
    label: 'Tea',
    EK: [
      { commodityCode: '0185', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0185', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0185', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TEA INSTANT ',
    label: 'Instant Tea',
    EK: [
      { commodityCode: '0186', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0186', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BEVERAGES,SYRUPS,FLAVOURS',
    label: 'Beverages, Syrups, Flavors',
    EK: [
      { commodityCode: '0190', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0190', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUIT JUICES',
    label: 'Fruit Juices',
    EK: [
      { commodityCode: '0192', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0192', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'EXTRACTS, FLAVOURING COMPOUND',
    label: 'Extracts, flavoring compound',
    EK: [
      { commodityCode: '0194', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0194', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'DAIRY PRODUCTS,EGGS',
    label: 'Dairy Products, Eggs',
    EK: [
      { commodityCode: '0200', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0200', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'DAIRY PRODUCTS EXCL EGGS ',
    label: 'Dairy Products Excluding Eggs',
    EK: [
      { commodityCode: '0201', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0201', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MILK FRESH ',
    label: 'Milk Fresh',
    EK: [
      { commodityCode: '0203', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0203', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BUTTER',
    label: 'Butter',
    EK: [
      { commodityCode: '0210', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0210', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEESE',
    label: 'Cheese',
    EK: [
      { commodityCode: '0220', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0220', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'EGGS',
    label: 'Eggs',
    EK: [
      { commodityCode: '0230', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0230', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ICE CREAM,FROZEN CONFECTION',
    label: 'Ice Cream, Frozen Confectionery',
    EK: [
      { commodityCode: '0246', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0246', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH (EDIBLE), SEAFOOD',
    label: 'Fish (Edible), Seafood',
    EK: [
      { commodityCode: '0300', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0300', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH  EDIBLE ,SEAFOOD  EXCL CAVIAR ',
    label: 'Fish, Edible, Seafood, excluding...',
    EK: [
      { commodityCode: '0301', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0301', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****SEAFOOD',
    label: 'Seafood',
    EK: [
      { commodityCode: '0302', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0302', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SEA URCHIN/PROCESSED',
    label: 'Sea Urchin Processed',
    EK: [
      { commodityCode: '0304', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0304', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SEA-URCHIN',
    label: 'Sea Urchin',
    EK: [
      { commodityCode: '0305', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0305', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH, FISH PRODUCTS',
    label: 'Fish, Fish Products',
    sortPosition: 5,
    EK: [
      { commodityCode: '0306', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0306', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH PROCESSED ',
    label: 'Fish Processed',
    EK: [
      { commodityCode: '0307', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0307', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH SMOKED ',
    label: 'Fish Spices',
    EK: [
      { commodityCode: '0308', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0308', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0308', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SHELLFISH',
    label: 'Shellfish',
    EK: [
      { commodityCode: '0309', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0309', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CLAMS,OYSTERS,SCALLOPS',
    label: 'Clams, Oysters, Scallops',
    EK: [
      { commodityCode: '0310', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0310', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****COCKLES',
    label: 'Cockles',
    EK: [
      { commodityCode: '0311', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0311', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ABALONE',
    label: 'Abalone',
    EK: [
      { commodityCode: '0314', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0314', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CRABS,CRAWFISH,LOBSTERS      ',
    label: 'Crabs, Crawfish, Lobsters',
    sortPosition: 4,
    EK: [
      { commodityCode: '0315', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0315', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CRABS',
    label: 'Crabs',
    EK: [
      { commodityCode: '0316', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0316', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TILAPIA',
    label: 'Tilapia',
    EK: [
      { commodityCode: '0318', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0318', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MAHI MAHI',
    label: 'MAHI-MAHI',
    EK: [
      { commodityCode: '0319', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0319', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'EEL',
    label: 'Eel',
    EK: [
      { commodityCode: '0320', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0320', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TUNA  FRESH ',
    label: 'Tuna Fish',
    EK: [
      { commodityCode: '0323', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0323', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH EDIBLE/EXCL.LOBSTERS,SCALLOPS ',
    label: 'Fish, edible, excluding lobsters',
    EK: [
      { commodityCode: '0324', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0324', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH EDIBLE ',
    label: 'Fish Edible',
    EK: [
      { commodityCode: '0326', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0326', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH ROE',
    label: 'Fish Roe',
    EK: [
      { commodityCode: '0330', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0330', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH GUT  DRIED ',
    label: 'Fish Gut Dried',
    EK: [
      { commodityCode: '0334', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0334', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FROGS',
    label: 'Frogs',
    EK: [
      { commodityCode: '0335', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0335', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SPIRITS,LIQUORS,WINES,BEERS',
    label: 'Spirits, Liquors, Wines',
    EK: [
      { commodityCode: '0129', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0129', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ASAFOETIDA',
    label: 'Asafoetida',
    EK: [
      { commodityCode: '0737', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '0737', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0737', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'Equine Equipment',
    label: 'Equine Equipment',
    EK: [{ commodityCode: '1051', product: 'GCR', productSHC: '', commoditySHC: '' }],
  },
  {
    value: 'LEATHER,LEATHER PRODUCTS EXCL. SPORTING GOODS',
    label: 'Leather, Leather Products Excluding Sporting Goods',
    EK: [
      { commodityCode: '1210', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1210', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER,HIDES,SKINS - TANNED, PROCESSED',
    label: 'Leather, Hides, Skins - Tanned, Prepared',
    EK: [
      { commodityCode: '1211', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1211', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FURS,HIDES,SKINS-UNDRESED/INCL SEMI-FINISHED MFRS',
    label: 'Furs, hides, skins - undressed, including semi-finished',
    EK: [
      { commodityCode: '1213', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1213', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '*****TANNED AND/OR PROCESSED LEATHER, SYNTHETIC LEATHER CUT TO',
    label: 'Tanned and/or Processed Leather, Synthetic Leather Cut to Shape',
    EK: [
      { commodityCode: '1214', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1214', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HIDES,SKINS - INCLUDING PRODUCTS THEREOF',
    label: 'Hides, Skins - Including Products Thereof',
    EK: [
      { commodityCode: '1215', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1215', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDBAGS, POCKETBOOKS, WALLETS, PURSES',
    label: 'Handbags, Pocketbooks, Wallets',
    EK: [
      { commodityCode: '1231', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1231', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDBAGS, BELTS - MADE OF LEATHER',
    label: 'Handbags, Belts - Made of Leather',
    EK: [
      { commodityCode: '1234', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1234', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER  EXCL NUTRIA/REPTILE SKIN MANUACTURES ',
    label: 'Leather Excluding Nutria/Reptile Skin Manufactures',
    EK: [
      { commodityCode: '1237', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1237', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LEATHER-BAG,BALL,BELT,BILLFOLD,SUITCASE,HARNESS,SADDLERY,POC',
    label: 'Leather bags, balls, belts, billfolds, suitcases, and harnesses.',
    EK: [
      { commodityCode: '1238', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1238', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAR SEAT COVERS,RUGS-MADE OF HIDE OR SHEEPSKIN',
    label: 'Car Seat Covers, Rugs made of Hide or Sheep-Skin',
    EK: [
      { commodityCode: '1251', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1251', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BAGS,BELTS,BILLFOLDS,BRUSHES,CLASPS,SEWING KITS,SHAVING KITS',
    label: 'Bags, Belts, Billfolds, Brushes, Clasps, Sewing Kits, Shaving Kits',
    EK: [
      { commodityCode: '1295', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1295', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEEDS',
    label: 'Seeds',
    EK: [
      { commodityCode: '1485', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1485', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1485', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TOBACCO, TOBACCO PRODUCTS',
    label: 'Tobacco, Tobacco Products',
    EK: [
      { commodityCode: '1550', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1550', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '*****CIGARETTES, CIGARS AND/OR TOBACCO',
    label: 'Cigarettes, Cigars, and/or Tobacco',
    EK: [
      { commodityCode: '1563', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1563', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OILS  ANIMAL, VEGETABLE ',
    label: 'Oils, Animal, Vegetable',
    EK: [
      { commodityCode: '1700', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1700', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OLIVE OIL',
    label: 'Olive Oil',
    EK: [
      { commodityCode: '1701', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1701', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ESSENTIAL OILS',
    label: 'Essential Oils',
    EK: [
      { commodityCode: '1716', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1716', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OIL COMPOUNDS  FOR PAINT ',
    label: 'Oil Compounds for Paint',
    EK: [
      { commodityCode: '1728', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1728', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BEESWAS',
    label: 'Beeswax',
    EK: [
      { commodityCode: '1904', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1904', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BRISTLES     ',
    label: 'Bristles',
    EK: [
      { commodityCode: '1915', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1915', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'STOMACH  DRIED ',
    label: 'Stomach, Dried',
    EK: [
      { commodityCode: '1923', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1923', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CASINGS  MADE OF ANIMAL INTESTINES, GUT ',
    label: 'Casings made of animal intestines, gut',
    EK: [
      { commodityCode: '1925', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1925', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GLANDS  ANIMAL ',
    label: 'Animal Glands',
    EK: [
      { commodityCode: '1931', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1931', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FEATHERS, FEATHER TRIMMINGS',
    label: 'Feathers, Feather Trimmings',
    EK: [
      { commodityCode: '1950', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1950', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FEATHERS  OSTRICH ',
    label: 'Feathers, Ostrich',
    EK: [
      { commodityCode: '1951', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1951', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEA-FLORA, SHELLS',
    label: 'Sea Flora, Sea Sponges',
    EK: [
      { commodityCode: '1970', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1970', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SPONGES  NATURAL ',
    label: 'Sponges, Natural',
    EK: [
      { commodityCode: '1980', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1980', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SPONGES  ARTIFICIAL ',
    label: 'Sponges, Artificial',
    EK: [
      { commodityCode: '1981', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1981', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LIVE AQUATIC PLANTS, CORAL',
    label: 'Live Aquatic Plants, Coral',
    EK: [
      { commodityCode: '1983', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1983', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ANIMAL HUNTING TROPHIES  EXCLUDING MORY ',
    label: 'Animal Hunting Trophies Excluding Ivory',
    EK: [
      { commodityCode: '1993', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '1993', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'YARN,THREAD,FIBRES, TEXTILE MANUFACTURES',
    label: 'Yarn, Thread, Fibres, Textile Manufactures',
    EK: [
      { commodityCode: '2001', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2001', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'YARN, THREAD, FIBRES',
    label: 'Yarn, Thread, Fibres',
    EK: [
      { commodityCode: '2002', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2002', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COTTON, YARN, THREAD',
    label: 'Cotton, Yarn, Thread',
    EK: [
      { commodityCode: '2020', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2020', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOOL, YARN, THREAD',
    label: 'Wool, Yarn, Thread',
    EK: [
      { commodityCode: '2090', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2090', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CLOTH NOT FURTHER PROCESSSED OR MANUFACTURED/EXCLUS. BALES/B',
    label: 'Cloth not further processed or manufactured, exclusively Bales',
    EK: [
      { commodityCode: '2102', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2102', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VISCOSE CLOTH NOT FRTHR PRCSSD/MFD/EXCLSVLY IN BALES,BOLTS,P',
    label: 'Viscose cloth, not further processed, manufactured, or exclusively in',
    EK: [
      { commodityCode: '2104', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2104', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC COATED FABRICS NOT FRTHR PRCSSD/MFD/EXCLSVLY IN BALE',
    label: 'Plastic Coated Fabrics, Not Further Processed or Manufactured',
    EK: [
      { commodityCode: '2144', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2144', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PIECE GOODS  MADE OF NYLON ',
    label: 'Piece Goods Made of Nylon',
    EK: [
      { commodityCode: '2162', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2162', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '*****BATIK TEXTILES',
    label: 'Batik Textiles',
    EK: [
      { commodityCode: '2183', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2183', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHEEP SKIN COATS,OVERCOATS,HATS,SLIPPERS-HANDMADE',
    label: 'Sheepskin Coats, Overcoats, Hats, Slippers',
    EK: [
      { commodityCode: '2189', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2189', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'YARN,THREAD,FIBERS IN BALES/BOLTS/PCS AND BEDSHEETS ETC MADE O',
    label: 'Yarn, Thread, Fibers in Bales, Bolts, Pieces, and Bed Sheets',
    EK: [
      { commodityCode: '2192', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2192', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, YARN, THREAD, TEXTILE MANUFACTURES, WEARING APPAREL EXCLUDING FOOTWEAR, LEATHER MANUFACTUR',
    label: 'Garments, Yarn, Thread, Textile Manufactures, Wearing Apparel excluding Footwear, Leather Manufactures',
    EK: [
      { commodityCode: '2194', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2194', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'A: YARN, THREAD, FIBRES, CLOTH - NOT FURTHER PROCESSED OR MANUFACTURED/EXCLUSIVELY IN BALES, BOLTS, ',
    label: 'Yarn, thread, fibers, cloth - not further processed or manufactured, exclusively in bales.',
    EK: [
      { commodityCode: '2195', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2195', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'YARN,THREAD,FIBRES,CLOTH-NOT FURTHER PROCESSED OR MANUFACTUR',
    label: 'Yarn, thread, fibers, cloth - not further processed or manufactured.',
    EK: [
      { commodityCode: '2197', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2197', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'YARN,THREAD,FIBRE,TEXTILE,EXCL.WEARING A',
    label: 'Yarn, Thread, Fibre, Textile, excluding Wearing Apparel',
    EK: [
      { commodityCode: '2198', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2198', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'A: YARN, THREAD, FIBRES, TEXTILES, B: TEXTILE MANUFACTURES, C: GARMENTS, WEARING APPAREL',
    label: 'Yarn, Thread, Fibers, Textiles, Textile Manufactures, Garments',
    EK: [
      { commodityCode: '2199', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2199', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL',
    label: 'Garments, Wearing Apparel',
    EK: [
      { commodityCode: '2200', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL (EXCLUDING UMBRELLAS, BILLFOLDS, PURSES, CARRYING CASES, HANDBAGS, COSTUME',
    label: 'Garments, wearing apparel (excluding umbrellas, billfolds, purses, carrying cases, etc.)',
    EK: [
      { commodityCode: '2203', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2203', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, CLOTHING AND WEARING APPAREL EXCLUDING LEATHER MANUFACTURES',
    label: 'Garments, Clothing, and Wearing Apparel, excluding Leather Manufactures',
    EK: [
      { commodityCode: '2204', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2204', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EYELETS,PRESS FASTENERS,RIVETS, ELECTRIC ATTACHING MACHINE',
    label: 'Eyelets, press fasteners, rivets, electric attaching machine',
    EK: [
      { commodityCode: '2205', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2205', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL, SHOE LACES, HOOKS, SNAPS, BUTTONS, BUCKLES, ZIP-FASTENERS EXCLUDING FURS,',
    label: 'Garments, Wearing Apparel, Shoe Laces, Hooks, Snaps, Buttons, Buckles, Zipper Fasteners',
    EK: [
      { commodityCode: '2206', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL, SHOE LACES, HOOKS, SNAPS, BUTTONS,BUCKLES, ZIP-FASTENERS EXCLUDING FOOTWE',
    label: 'Garments, wearing apparel, shoelaces, hooks, snaps, buttons, buckles, zip fasteners',
    EK: [
      { commodityCode: '2207', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2207', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, YARN, THREAD, FIBRES - NOT FURTHER PROCESSED OR MANUFACTURED/EXCLUSIVELY IN BALES, BOLTS, ',
    label: 'Garments, Yarn, Thread, Fibers - Not Further Processed or Manufactured/Exclusively in Bales',
    EK: [
      { commodityCode: '2211', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2211', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TROUSERS, SHIRTS',
    label: 'Trousers, Shirts',
    EK: [
      { commodityCode: '2214', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2214', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CLOTH,LADIES CUT DRESSES,SEMI-FINISHED CLOTHIN INCL DRESSMAK',
    label: 'Cloth, Ladies Cut Dresses, Semi-Finished Clothing Including Dressmaking',
    EK: [
      { commodityCode: '2215', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2215', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRESSES  LADIES, FINISHED ',
    label: "Ladies' Finished Dresses",
    EK: [
      { commodityCode: '2218', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2218', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TERRY CLOTH, TERRY ARTICLES',
    label: 'Terry Cloth, Terry Articles',
    EK: [
      { commodityCode: '2222', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2222', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL (FUR/LEATHER)',
    label: 'Garments, Wearing Apparel (Fur)',
    EK: [
      { commodityCode: '2234', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2234', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OVERCOATS,JACKETS,SKIRTS,SHIRTS,WAISTCOATS,TROUSERS-LEATHER',
    label: 'Overcoats, Jackets, Skirts, Shirts, Waistcoats, and so on.',
    EK: [
      { commodityCode: '2243', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2243', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BLOUSES,JACKETS,JEAN,SKIRT,TROUSER,WAISTCOAT-MADE OF COTTON',
    label: 'Blouses, Jackets, Jeans, Skirts, Trousers, Waistcoats',
    EK: [
      { commodityCode: '2292', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2292', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CORSETS, GIRDLES, BRASSIERES',
    label: 'Corsets, Girdles, Brassieres',
    EK: [
      { commodityCode: '2306', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2306', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, CHILDRENS WEARING APPAREL',
    label: "Garments, Children's Wearing Apparel",
    EK: [
      { commodityCode: '2309', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2309', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL, TEXTILE MANUFACTURES - ON HANGERS -',
    label: 'Garments, Wearing Apparel, Textile Manufactures - On Hangers',
    EK: [
      { commodityCode: '2311', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2311', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GLOVES, MITTENS',
    label: 'Gloves, Mittens',
    EK: [
      { commodityCode: '2332', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2332', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HATS',
    label: 'Hats',
    EK: [
      { commodityCode: '2350', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2350', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'STOCKINGS, ANKLETS, PEDS, SOCKS',
    label: 'Stockings, Anklets, Peds, Socks',
    EK: [
      { commodityCode: '2360', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2360', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOSE, PANTYHOSE',
    label: 'Hose, Pantyhose',
    EK: [
      { commodityCode: '2367', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2367', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHIRTS, BLOUSES, UNDERWEAR',
    label: 'Shirts, blouses, underwear',
    EK: [
      { commodityCode: '2408', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2408', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHOES',
    label: 'Shoes',
    EK: [
      { commodityCode: '2415', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2415', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SHOES, SLIPPERS',
    label: 'Shoes, Slippers',
    EK: [
      { commodityCode: '2420', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2420', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SOLES  FOR SHOES, SLIPPERS ',
    label: 'Soles for Shoes, Slippers',
    EK: [
      { commodityCode: '2426', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2426', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '*****WOOLLEN KNITTED SWEATERS AND DRESSES',
    label: 'Woolen Knitted Sweaters and',
    EK: [
      { commodityCode: '2451', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2451', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PULLOVERS, CARDIGANS',
    label: 'Pullovers, Cardigans',
    EK: [
      { commodityCode: '2452', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2452', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GARMENTS, WEARING APPAREL (KNITTED)',
    label: 'Garments, Wearing Apparel (Knitted)',
    EK: [
      { commodityCode: '2455', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2455', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'UNDERWEAR, SWIMWEAR, BODY SUPPORTING GARMENTS',
    label: 'Underwear, Swimwear, Body Supporting Garments',
    EK: [
      { commodityCode: '2461', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2461', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRAWNWORK, NEEDLEWORK, EMBROIDERIES, LACE',
    label: 'Drawnwork, Needlework, Embroideries',
    EK: [
      { commodityCode: '2500', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2500', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TEXTILE ARTICLES  FNSHD/EXCL WEARING APPAREL ',
    label: 'Textile Articles, Finished, Excluding Wearing Apparel',
    EK: [
      { commodityCode: '2600', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2600', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GAS MANTLES',
    label: 'Gas Mantles',
    EK: [
      { commodityCode: '2627', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2627', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HAIR, HAIR MANUFACTURES',
    label: 'Hair, Hair Manufactures',
    EK: [
      { commodityCode: '2700', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2700', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOUPEES,WIGS,SWITCHES,FALSE EYELASHES',
    label: 'Toupees, Wigs, Switches, False Eyelashes',
    EK: [
      { commodityCode: '2741', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2741', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BLANKETS,BEDSHEETS/SPREADS,PILLOW CASES,MOSQUITO NETS,TOWELS',
    label: 'Blankets, Bedsheets/Spreads, Pillow Cases, Mosquito Nets',
    EK: [
      { commodityCode: '2811', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2811', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CARPETS, RUGS',
    label: 'Carpets, Rugs',
    EK: [
      { commodityCode: '2865', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2865', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CANVAS ARTICLES',
    label: 'Canvas Articles',
    EK: [
      { commodityCode: '2920', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2920', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '***** CLOTH BLANKETS,CLOTH SLEEPING BAGS',
    label: 'Cloth Blankets, Cloth Sleeping Bags',
    EK: [
      { commodityCode: '2949', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '2949', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'METAL INGOTS/GRAIN  SEMI-FINISHED  SHEET, FOIL, POWDER, SPO',
    label: 'Metal Ingots/Grain Semi-Finished Sheet, Foil, Plates',
    EK: [
      { commodityCode: '3002', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3002', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WIRE CLOTH',
    label: 'Wire Cloth',
    EK: [
      { commodityCode: '3004', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3004', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLATINOID MINERAL CONCENTRADES, MATTE',
    label: 'Platinoid Mineral Concentrates, Matte',
    EK: [
      { commodityCode: '3011', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3011', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SILVER-BULLION,INGOTS,SPECIE,GRAIN,FOIL.',
    label: 'Silver Bullion, Ingots, Specie, Grain',
    EK: [
      { commodityCode: '3015', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3015', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'METAL WARES  EXCL GOLD, PLATINUM, PLATINUM MTRLS ',
    label: 'Metal Wares Excluding Gold, Platinum, Platinum Material.',
    EK: [
      { commodityCode: '3025', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3025', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COPPER MANUFACTURS',
    label: 'Copper Manufactures',
    EK: [
      { commodityCode: '3030', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3030', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COPPER IN SLABS, SEMI-FINISHD PRODS,PLATES,SHEETS,WIRE,ROD,',
    label: 'Copper in Slabs, Semi-Finished Products, Plates, Sheets, Wire',
    EK: [
      { commodityCode: '3032', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3032', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COPPER ORE',
    label: 'Copper Ore',
    EK: [
      { commodityCode: '3034', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3034', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COPPER WIRE, COPPER CABLE',
    label: 'Copper Wire, Copper Cable',
    EK: [
      { commodityCode: '3038', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3038', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BRASS MANUFACTURS',
    label: 'Brass Manufactures',
    EK: [
      { commodityCode: '3050', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3050', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ALUMINIUM MANUFACTURES',
    label: 'Aluminium Manufactures',
    EK: [
      { commodityCode: '3060', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3060', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EXTRUSIONS,HONEYCOMB SHEETS,SHEETS,FOIL,PLATES,TUBES,RODS,',
    label: 'Extrusions, Honeycomb Sheets, Sheets, Foil, Plates, etc.',
    EK: [
      { commodityCode: '3075', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3075', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CUTLERY',
    label: 'Cutlery',
    EK: [
      { commodityCode: '3100', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3100', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CUTLER, FORKS, SPOONS EXCLDG SILVERWARE',
    label: 'Cutlery, Forks, Spoons Excluding Silver',
    EK: [
      { commodityCode: '3101', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3101', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RAZORS, RAZOR BLADES         ',
    label: 'Razors, Razor Blades',
    EK: [
      { commodityCode: '3127', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3127', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TABLE FLATWARE, BRONZEWARE',
    label: 'Table Flatware, Bronzeware',
    EK: [
      { commodityCode: '3150', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3150', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTROPLATED NICKEL SILVERWARE,TABLEWARE,TROPHIES,CUPS,MEDA',
    label: 'Electroplated Nickel Silverware, Tableware, Trophies, Cups',
    EK: [
      { commodityCode: '3153', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3153', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LAWN SPRINKLERS,PISTOL NOZZLE/CONNECTORS,BASES USD FOR SPRIN',
    label: 'Lawn Sprinklers, Pistol Nozzle and Connectors, Bases Used',
    EK: [
      { commodityCode: '3206', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRILL BITS',
    label: 'Drill Bits',
    EK: [
      { commodityCode: '3236', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3236', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HARDWARE',
    label: 'Hardware',
    EK: [
      { commodityCode: '3300', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3300', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BUILDING MTRLS, FIXTURS, FITTNGS, EXCLDG TOOLS',
    label: 'Building Materials, Fixtures, Fittings, etc.',
    EK: [
      { commodityCode: '3321', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3321', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BATHROOM SHOWER SETS',
    label: 'Bathroom Shower Sets',
    EK: [
      { commodityCode: '3341', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3341', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD/TABLE/KITCHEN /HOSPITAL UTENSILS',
    label: 'Household/Table/Kitchen/Hospital Utensils',
    EK: [
      { commodityCode: '3400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TABLE/KITCHEN UTENSLS OF STNLESS STEEL EXCL MACHI,ELEC EQPT',
    label: 'Table and Kitchen Utensils of Stainless Steel Excluding Machinery',
    EK: [
      { commodityCode: '3404', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3404', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'KITCHEN/TABLE UTENSLS,VASES EXCL ELECTRIC EQUIPMENT',
    label: 'Kitchen and Table Utensils, Vases, Excluding Electric Equipment',
    EK: [
      { commodityCode: '3409', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3409', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'METAL KITCHENWARE',
    label: 'Metal Kitchenware',
    EK: [
      { commodityCode: '3411', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3411', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DOOR LOCKS, HANDLES',
    label: 'Door Locks, Handles',
    EK: [
      { commodityCode: '3501', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3501', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PUMPING FIXTURES, PIPE FIXTURES',
    label: 'Pumping Fixtures, Pipe Fixtures',
    EK: [
      { commodityCode: '3503', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3503', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HARDWARE FOR FURNITURE',
    label: 'Hardware for Furniture',
    EK: [
      { commodityCode: '3504', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3504', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS FOR REFRIGERATORS/COOKERS',
    label: 'Parts for Refrigerators and Cookers',
    EK: [
      { commodityCode: '3574', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3574', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COBALT',
    label: 'Cobalt',
    EK: [
      { commodityCode: '3890', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3890', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ORE  EXCLUDING GOLD, SILVER, PLATINUM ',
    label: 'Ore, Excluding Gold, Silver, Platinum',
    EK: [
      { commodityCode: '3895', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3895', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SILVER ORE',
    label: 'Silver Ore',
    EK: [
      { commodityCode: '3896', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3896', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'METAL PRODUCTS  MISCELLANEOUS ',
    label: 'Metal Products Miscellaneous',
    sortPosition: 8,
    EK: [
      { commodityCode: '3900', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3900', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOULDS, DIES',
    label: 'Molds, Dies',
    EK: [
      { commodityCode: '3963', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3963', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LOCKS',
    label: 'Locks',
    EK: [
      { commodityCode: '3969', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3969', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CABLES,MESH,RIVET,SHEETS,TUBNGS,WIRE EXCL GOLD,SILVER,PLATIN',
    label: 'Cables, Mesh, Rivets, Sheets, Tubing, Wire, excluding Gold.',
    EK: [
      { commodityCode: '3991', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '3991', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BUSI/OFC MACHI INCL TOOLS,SURFC VHCLS EXCL STEAM/MOTORSHIP M',
    label: 'Business and Office Machines Including Tools, Surface Vehicles Excluding Steam',
    EK: [
      { commodityCode: '4010', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4010', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT',
    label: 'Aircraft',
    EK: [
      { commodityCode: '4105', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4105', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'HELICOPTER SPARES',
    label: 'Helicopter Spares',
    EK: [
      { commodityCode: '4107', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4107', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT PARTS  EXCL AIRCRAFT ENGINES ',
    label: 'Aircraft Parts, Excluding Aircraft Engines',
    EK: [
      { commodityCode: '4108', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4108', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '4108', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'A/CRAFT ENGNS,PARTS EXCL TAIL ASSMBLS,STABLISRS,WINGS,FUSELA',
    label: 'Aircraft Engines, Parts excluding Tail Assemblies, Stabilizers, etc.',
    EK: [
      { commodityCode: '4109', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4109', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT ENGINES,AIRCRAFT PARTS EXCL.FUSELAGES, WINGS ',
    label: 'Aircraft Engines, Aircraft Parts Excluding Fuselages, Wings',
    EK: [
      { commodityCode: '4110', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4110', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '4110', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'A/CRAFT ENGNS/PARTS INCL ACCESSRS EXCL OVRHAUL/MAINT TOOLS,S',
    label: 'Aircraft Engines and Parts, Including Accessories, Excluding Overhaul',
    EK: [
      { commodityCode: '4112', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4112', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT PARTS',
    label: 'Aircraft Parts',
    sortPosition: 6,
    EK: [
      { commodityCode: '4119', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4119', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '4119', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'AIRCRAFT PARTS  INCLDG ACCESSORIES ',
    label: 'Aircraft Parts Including Accessories',
    EK: [
      { commodityCode: '4123', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4123', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '4123', product: 'AOG', productSHC: 'AOG', commoditySHC: '' },
    ],
  },
  {
    value: 'OUTBOARD MOTORS  INCL ACCESSORIES ',
    label: 'Outboard Motors, Including Accessories',
    EK: [
      { commodityCode: '4181', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4181', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFACE VEHICLES,PARTS,ACCESSORIES',
    label: 'Surface Vehicles, Parts, Accessories',
    EK: [
      { commodityCode: '4200', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFAC VEHICLS EXCL STEAMSHIP/MOTORSHIP PARTS ',
    label: 'Surface Vehicles Excluding Steamship or Motorsip',
    EK: [
      { commodityCode: '4201', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4201', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VEHICLES AND ACCESSORIES EXCL STEAM/MOTOR/RAIL/ARGI.MACH.PARTS',
    label: 'Vehicles and Accessories Excluding Steam, Motor, Rail, Agricultural Machinery Parts',
    EK: [
      { commodityCode: '4203', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4203', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFAC VEHICLS,SELF-PROPELLED AGRI MACH-INCL ACCESRS-EXCL ST',
    label: 'Surface Vehicles, Self-Propelled Agricultural Machinery - Including Accessories',
    EK: [
      { commodityCode: '4206', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURFACE VHCLS,SELF-PPLD AGRO MACH-INCL ACCSR-EXCL STEAM/MOTO',
    label:
      'Surface Vehicles, Self-Propelled Agricultural Machines - Including Accessories - Excluding Steam or Motor Ship Machines',
    EK: [
      { commodityCode: '4209', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4209', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS OF AUTOMOBILE INCL ACCESSORIES /REPAIR EQUIPT',
    label: 'Parts of Automobile including Accessories/Repair Equipment',
    sortPosition: 15,
    EK: [
      { commodityCode: '4211', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4211', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMOBL PARTS INCL ACCESRS MOTORSCOOTRS,MTR/BICYCLES,AGRI M',
    label: 'Automobile Parts including Accessories for Motorcycles, Motor or Bicycles',
    EK: [
      { commodityCode: '4232', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4232', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOTORSCOOTERS,MOTORCYCLES,CYCLES,PART OF SURFACE VEHICLES,IN',
    label: 'Motor Scooters, Motorcycles, Cycles, Parts of Surface Vehicles, including',
    EK: [
      { commodityCode: '4235', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4235', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMOBILE TEMPERATURE GAUGES',
    label: 'Automobile Temperature Gauges',
    EK: [
      { commodityCode: '4242', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4242', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BRAKE LININGS',
    label: 'Brake Linings',
    EK: [
      { commodityCode: '4246', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4246', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FANBELTS',
    label: 'Fan Belts',
    EK: [
      { commodityCode: '4248', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4248', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS FOR SPORTING BOATS  INCL ACCESSORIES ',
    label: 'Parts for Sporting Boats, Including Accessories',
    EK: [
      { commodityCode: '4266', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4266', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FILTERS FOR AGRICULTURAL MACHINES',
    label: 'Filters for Agricultural Machines',
    EK: [
      { commodityCode: '4291', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4291', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OFFICE MACHINERY',
    label: 'Office Machinery',
    EK: [
      { commodityCode: '4300', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4300', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DATA PROCSSG EQPT,OFC MACHNRY,INCL ACCESSORIES,SUPPLIES',
    label: 'Data Processing Equipment, Office Machinery, Including Accessories',
    EK: [
      { commodityCode: '4302', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4302', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRNC DATA STORAGE PROCSG MACHINES,DIGITAL COMPUTRS,ANALO',
    label: 'Electronic Data Storage Processing Machines, Digital Computers, Analog',
    EK: [
      { commodityCode: '4303', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4303', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHOTO-TYPESETTING MACHINES',
    label: 'Photo-Typesetting Machines',
    EK: [
      { commodityCode: '4304', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4304', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COMPUTERS',
    label: 'Computers',
    sortPosition: 9,
    EK: [
      { commodityCode: '4306', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4306', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADDING/CALCULATNG MACHNS,CASH REGSTR,DUPLCTG DOCMTS,TYPEWRTR',
    label: 'Adding/Calculating Machines, Cash Register, Duplicating Documents',
    EK: [
      { commodityCode: '4309', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4309', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADDING/COMPUTING/TYPEWRTG MACHNS,DOCUMT/LTR DUPLCTG MACHNS',
    label: 'Adding, Computing, Typewriting Machines, Document, Letter Duplicating Machines',
    EK: [
      { commodityCode: '4311', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4311', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADDG/CALCULATG MACHNS,CASH RGSTERS,MACHNSFOR DUPLCTNG DOCS',
    label: 'Adding and Calculating Machines, Cash Registers, Machines for Duplicating',
    EK: [
      { commodityCode: '4312', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4312', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COMPUTER PUNCH CARDS, COMPUTER DATA SHEETS',
    label: 'Computer Punch Cards, Computer Data Sheets',
    EK: [
      { commodityCode: '4313', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4313', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELCTRNC CMPONNTS FABRICATD FR ELEC COMPTRS,AUXILIARY MACHNS ',
    label: 'Electronic Components Fabricated From Electronic Computers, Auxiliary',
    EK: [
      { commodityCode: '4314', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4314', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MAGNETIC TAPES FOR DATA STORAGE',
    label: 'Magnetic Tapes for Data Storage',
    EK: [
      { commodityCode: '4315', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4315', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BUSINESS AND OFFICE MACHINERY, PARTS, ACCESSORIES',
    label: 'Business and Office Machinery, Parts, Accessories',
    EK: [
      { commodityCode: '4316', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4316', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PUNCH CARS,TAPE,DATA SHEETS/DISCS FR ELCTRNC DATA STORAGE-PR',
    label: 'Punch Cards, Tape, Data Sheets/Discs from Electronic Data Processing Machines',
    EK: [
      { commodityCode: '4317', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4317', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADDG/CALCULATG MACHNS,CASH REGSTERS,MACHN FR DUPL DOCS TAPE,',
    label: 'Adding/Calculating Machines, Cash Registers, Machine For Duplicating',
    EK: [
      { commodityCode: '4318', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4318', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC DATA PROCSSG MACHNS,DIGTL/ANALOGUE COMPTRS-INDIVIDL UNI',
    label: 'Electrical Data Processing Machines, Digital/Analogue Computers',
    EK: [
      { commodityCode: '4319', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4319', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TYPEWRITERS',
    label: 'Typewriters',
    EK: [
      { commodityCode: '4321', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4321', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHOTOCOPYING MACHINS  INCL ACCESSORIES, SUPPLIES ',
    label: 'Photocopying Machines, Including Accessories and Supplies',
    EK: [
      { commodityCode: '4322', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4322', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRONIC PRODUCTS  INCL COMPONENTS ',
    label: 'Electronic Products Including Components',
    EK: [
      { commodityCode: '4323', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4323', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTER FOR PERSONAL COMPUTER',
    label: 'Printer for Personal Computer',
    EK: [
      { commodityCode: '4324', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4324', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DIGTL/ANALOG COMPUTRS INCL PUNCH CARDS,DISC PACKS,TAPES-ELEC',
    label: 'Digital/Analog Computers Including Punch Cards, Disc Packs, Tapes, Electronic',
    EK: [
      { commodityCode: '4326', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4326', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OFFICE MACHINES INCLUDING SUPPLIES ',
    label: 'Office Machines, Including Supplies',
    EK: [
      { commodityCode: '4327', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4327', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRONIC PRODUCTS',
    label: 'Electronic Products',
    EK: [
      { commodityCode: '4329', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4329', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WIRE PROCESSORS',
    label: 'Wire Processors',
    EK: [
      { commodityCode: '4330', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4330', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INTEGRATED CIRCUITS,RELAYS,CONDENSOR,CAPACITORS,TRANSISTORS,',
    label: 'Integrated Circuits, Relays, Condensers, Capacitors',
    EK: [
      { commodityCode: '4350', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4350', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TYPEWRITER RIBBONS',
    label: 'Typewriter Ribbons',
    EK: [
      { commodityCode: '4382', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4382', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC APPLIANCES, GAS APPLIANCES',
    label: 'Electric Appliances, Gas Appliances',
    EK: [
      { commodityCode: '4397', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4397', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC EQUIPMENT  EXCLUDING MACHINERY ',
    label: 'Electric Equipment Excluding Machinery',
    EK: [
      { commodityCode: '4400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC EQUIPMENT',
    sortPosition: 4,
    label: 'Electrical Equipment',
    EK: [
      { commodityCode: '4401', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4401', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC EQPT./APPL. EXCL. BUSINESS/OFFICE MACHINERY',
    label: 'Electric Equipment and Appliances excluding Business and Office Machinery',
    EK: [
      { commodityCode: '4402', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4402', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC CONTROL PANELS FOR RADAR STATION,POWERPLANT,HEATG/COOLG',
    label: 'Electrical Control Panels for Radar Station, Power Plant, Heating',
    EK: [
      { commodityCode: '4403', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4403', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRICAL APPLIANCES',
    label: 'Electrical Appliances',
    EK: [
      { commodityCode: '4404', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4404', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BATTERIES',
    label: 'Batteries',
    EK: [
      { commodityCode: '4405', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4405', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRODES',
    label: 'Electrodes',
    EK: [
      { commodityCode: '4409', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4409', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COMMUNICATION DEVICES, SIGNAL DEVICES',
    label: 'Communication Devices, Signal Devices',
    EK: [
      { commodityCode: '4410', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4410', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RADIOS,TELEVISIONS,RECORD PLAYERS,TAPE RECORDERS',
    label: 'Radios, Televisions, Record Players, Tape Recorders',
    EK: [
      { commodityCode: '4411', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4411', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC EQPT, ELEC APPLIANCES EXCL COMPUTERS,OFFICE MACHINERY',
    label: 'Electrical Equipment, Electrical Appliances, Excluding Computers, Office',
    EK: [
      { commodityCode: '4412', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4412', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RAIODS,TVS,RCRD PLAYRS,TAPE RCRDERS,LOUDSPKRS,AMPLIFIERS',
    label: 'Radios, Televisions, Record Players, Tape Recorders, Loudspeakers',
    EK: [
      { commodityCode: '4413', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4413', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LOUD SPKRS,TAPE RECORDRS,RCRD PLAYERS,RADIOS,AMPLIFIERS',
    label: 'Loudspeakers, Tape Recorders, Record Players, Radios',
    EK: [
      { commodityCode: '4414', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4414', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RAIODS, TAPE RECORDS, RECORD PLAYERS, AERIALS',
    label: 'Radios, Tape Recorders, Record Players, Aerials',
    EK: [
      { commodityCode: '4415', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4415', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOBILE PHONES, MOBILE PHONES,AUTOMOBILE RADIOS, DICTATION MACHINES, HEARING AIDS, RECORDS, RECORDING',
    label: 'Mobile Phones, Automobile Radios, Dictation Machines, Hearing Aids, Records, Recording',
    EK: [
      { commodityCode: '4416', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4416', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RADIOS,TV,RADIO-TV SETS,RADIO-PHONOGRAPH SETS,ELEC HSHLD APP ',
    label: 'Radios, Televisions, Radio-Television Sets, Radio-Phonograph Sets, Electrical',
    EK: [
      { commodityCode: '4417', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4417', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOBILE PHONES, TELEPHONE EQUIPMENT, RADIO-TELEPHONE EQUIPMENT - INCLUDING TESTING EQUIPMENT -',
    label: 'Mobile Phones, Telephone Equipment, Radio-Telephone Equipment - Including Testing Equipment',
    EK: [
      { commodityCode: '4419', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4419', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RADIOS, TV, RADIO-TV SETS, PHONOGRAPH SETS',
    label: 'Radios, Televisions, Radio-Television Sets, Phonographs',
    EK: [
      { commodityCode: '4420', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4420', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOBILE PHONE ACCESSORIES, PARTS/COMPONENTS FOR THE MANUFACTURE OF TELECOMMUNICATION EQUIPMENT',
    label: 'Mobile Phone Accessories, Parts and Components for the Manufacture of Telecommunication Equipment',
    EK: [
      { commodityCode: '4422', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4422', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CONDENSERS',
    label: 'Condensers',
    EK: [
      { commodityCode: '4423', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4423', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LOUDSPEAKERS',
    label: 'Loudspeakers',
    EK: [
      { commodityCode: '4425', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4425', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMBL RADIOS,DICTATN MACHNS,HEARING AIDS,RECORDS,RCRDG TAP',
    label: 'Automobile Radios, Dictation Machines, Hearing Aids, Records, etc.',
    EK: [
      { commodityCode: '4426', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4426', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC TUBES,TRANSISTORS,SOLID STATE SEMI-CONDUCTOR DEVICES,RA',
    label: 'Electrical Tubes, Transistors, Solid State Semiconductor Devices',
    EK: [
      { commodityCode: '4427', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4427', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RELAYS',
    label: 'Relays',
    EK: [
      { commodityCode: '4428', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4428', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AMPLIFIERS,SPEAKERS,RHYTHM BOXES,RADIOS',
    label: 'Amplifiers, Speakers, Rhythm Boxes',
    EK: [
      { commodityCode: '4429', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4429', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC CONDENSERS,RESISTORS,RHEOSTATS',
    label: 'Electric Condensers, Resistors, Rheostats',
    EK: [
      { commodityCode: '4430', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4430', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RESISTORS,RHEOSTATS,ELECTRIC SWITCHES',
    label: 'Resistors, Rheostats, Electric Switches',
    EK: [
      { commodityCode: '4431', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4431', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TUBES, VALVES - FOR RADIOS, TELEVISIONS',
    label: 'Tubes, Valves - For Radios, Televisions',
    EK: [
      { commodityCode: '4433', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4433', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SWITCHES',
    label: 'Switches',
    EK: [
      { commodityCode: '4434', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4434', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC TUBES,TRANSISTORS,SOLID STATE SEMI-CONDUCTOR DEVICES',
    label: 'Electronic Tubes, Transistors, Solid State Semiconductor',
    EK: [
      { commodityCode: '4435', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4435', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TRANSISTORS, ELECTRIC CONDENSERS',
    label: 'Transistors, Electric Condensers',
    EK: [
      { commodityCode: '4436', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4436', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RESISTORS,TRANSISTORS,CAPACITORS,REGULATORS,TUNERS',
    label: 'Resistors, Transistors, Capacitors, Regulators, Tubes',
    EK: [
      { commodityCode: '4439', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4439', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RADIOS,ELECTRONIC TUBES,TRANSISTORS,SOLID-STATE SEMI-CONDUCT',
    label: 'Radios, Electronic Tubes, Transistors, Solid-State Semiconductors',
    EK: [
      { commodityCode: '4440', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4440', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TELEVISIONS',
    label: 'Televisions',
    EK: [
      { commodityCode: '4442', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4442', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMBL-RADIOS, DICTATN MACHNS,HEARG AIDS,RECORDS,RCRDG TAPE',
    label: 'Automobile Radios, Dictation Machines, Hearing Aids, Recording',
    EK: [
      { commodityCode: '4443', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4443', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TRANSISTOR RADIOS',
    label: 'Transistor Radios',
    EK: [
      { commodityCode: '4444', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4444', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TELEGRAPH/TELEPHONE/TELETYPE APPARATUS',
    label: 'Telegraph/Telephone/Teletype Apparatus',
    EK: [
      { commodityCode: '4445', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4445', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INTEGRATD CIRCUITS,CONDENSORS,RELAYS,RESISTORS,TRANSISTORS',
    label: 'Integrated Circuits, Capacitors, Relays, Resistors',
    EK: [
      { commodityCode: '4446', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4446', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRON GUN WHICH IS COMPONENT OF TV RCVG TUBE MOUNT',
    label: 'Electron Gun Which is Component of TV Receiving Tube Mount',
    EK: [
      { commodityCode: '4447', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4447', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SMOKE DETECTORS',
    label: 'Government Owned Stores, Including Supplies',
    EK: [
      { commodityCode: '4449', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4449', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HOUSEHOLD APPLIANCES/UTENSILS - ELECTRIC -',
    label: 'Household Appliances/Utensils - Electric',
    EK: [
      { commodityCode: '4450', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4450', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUDIO/VIDEO APPARATUS,COMPUTERS-FOR DOMESTIC USE ONLY',
    label: 'Audio/Video Apparatus, Computers - For Domestic Use Only',
    EK: [
      { commodityCode: '4451', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4451', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRO EROSION MACHINERY',
    label: 'Electro Erosion Machinery',
    EK: [
      { commodityCode: '4452', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4452', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'IMPLMENTS FR MEASURING,CHKG,ANALYSG,AUTO CONTRLLG,CONDENSORS',
    label: 'Implements for Measuring, Checking, Analyzing, Auto Controlling',
    EK: [
      { commodityCode: '4455', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4455', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMBL RADIOS,SPEED CONTROL AMPLIFIERS,GRAPHIC EQLIZERS,PRE',
    label: 'Automobile Radios, Speed Control Amplifiers, Graphic Equalizers',
    EK: [
      { commodityCode: '4457', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4457', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS FOR RADIOS,RECORD PLAYERS,TELEVISIONS',
    label: 'Parts for Radios, Record Players, Televisions',
    EK: [
      { commodityCode: '4458', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4458', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRONIC PARTS',
    label: 'Electronic Parts',
    EK: [
      { commodityCode: '4459', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4459', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC RAZORS, MECHANICAL RAZORS',
    label: 'Electric Razors, Mechanical Razors',
    EK: [
      { commodityCode: '4461', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4461', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FRIDGE,FREEZERS,CLOTHES DRIERS,WASHING MACHINES,STOVES',
    label: 'Refrigerators, Freezers, Clothes Dryers, Washing Machines',
    EK: [
      { commodityCode: '4465', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4465', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'THERMOSTATS,SELECTOR SWITCHES,SWITCH DOORS',
    label: 'Thermostats, Selector Switches, Switch Doors',
    EK: [
      { commodityCode: '4467', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4467', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LAMPS,BULBS,TUBES-INCANDESCENT,FLUORESCENT',
    label: 'Lamps, Bulbs, Tubes - Incandescent',
    EK: [
      { commodityCode: '4470', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4470', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FANS, HEATERS - ELECTRIC',
    label: 'Fans, Heaters - Electric',
    EK: [
      { commodityCode: '4474', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4474', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LIGHTING FIXTURES, LIGHTING FITTING',
    label: 'Lighting Fixtures, Lighting Fitting',
    EK: [
      { commodityCode: '4475', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4475', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELECTRIC LAMPS, LAMP SHADES, CHNDELIERS',
    label: 'Electric Lamps, Lamp Shades, Chandeliers',
    EK: [
      { commodityCode: '4476', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4476', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WIRES,CABLES - ELECTRIC -',
    label: 'Wires, Cables - Electrical',
    EK: [
      { commodityCode: '4477', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4477', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AIR CONDITIONERS',
    label: 'Air Conditioners',
    EK: [
      { commodityCode: '4483', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4483', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUXLRY UNITX,CABLE-FORMS/CONN,FORMS,PACKS,RELAYS,SELECTORS,S',
    label: 'Auxiliary Units, Cable Forms, Connectors, Forms, Packs, Relays',
    EK: [
      { commodityCode: '4490', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4490', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELEC EQPT FR TELETYP,TELEGRAPH,TEL,RADIO-TEL,TV/RADIO BROADC',
    label: 'Electrical Equipment for Teletype, Telegraph, Telephone, Radio-Telephone, Television',
    EK: [
      { commodityCode: '4492', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4492', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ELE APPL./MACHINERY,MACHNRY TOOLS EXCL STEAM/MOTOR SHIP MACH',
    label: 'Electrical Appliances/Machinery, Machinery Tools excluding Steam/Motor Ship Machinery',
    EK: [
      { commodityCode: '4499', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4499', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEMI-CONDUCTORS',
    label: 'Semiconductors',
    EK: [
      { commodityCode: '4504', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4504', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED CIRCUITS',
    label: 'Printed Circuits',
    EK: [
      { commodityCode: '4505', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4505', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TELEVISION TUNERS',
    label: 'Television Tuners',
    EK: [
      { commodityCode: '4506', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4506', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VALVES,ELEC/MECH MACHINRY FR NUCLEAR,HYDRO-CARBON POWER PLAN',
    label: 'VALVES, electrical or mechanical machinery for nuclear, hydro-carbon power',
    EK: [
      { commodityCode: '4515', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4515', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOTORS, GENERATORS, TRANSFORMERS - ELECTRIC',
    label: 'Motors, Generators, Transformers - Electric',
    EK: [
      { commodityCode: '4560', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4560', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ENGINES,TURBINES',
    label: 'Engines, Turbines',
    EK: [
      { commodityCode: '4600', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4600', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INTERNAL COMBUSTION ENGINES,TURBINES',
    label: 'Internal Combustion Engines, Turbines',
    EK: [
      { commodityCode: '4609', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4609', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DIESEL ENGINES, PUMPSETS',
    label: 'Diesel Engines, Pumpsets',
    EK: [
      { commodityCode: '4616', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4616', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY TOOLS EXCL.STEAMSHIP/MOTORSHIP SPARE PARTS',
    label: 'Machinery Tools, Excluding Steamship or Motorshiop Spare Parts',
    EK: [
      { commodityCode: '4700', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4700', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY, ELECTRIC APPARATUS, TOOLS',
    label: 'Machinery, Electric Apparatus, Tools',
    EK: [
      { commodityCode: '4701', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4701', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY,TOOLS EXCL MACHNS FOR A/CRAFT ARRESTING,CATAPULTIN',
    label: 'Machinery and Tools excluding Machines for Aircraft Arresting and Catapulting',
    EK: [
      { commodityCode: '4702', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4702', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY AND TOOLS .......',
    label: 'Machinery and Tools',
    EK: [
      { commodityCode: '4703', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4703', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AGRICULTURAL EQUIPMENT',
    label: 'Agricultural Equipment',
    EK: [
      { commodityCode: '4704', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4704', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINES  FOR MFG BOTTLES, WASHING BOTTLES ',
    label: 'Machines for manufacturing bottles, washing bottles',
    EK: [
      { commodityCode: '4708', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4708', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINE PARTS',
    label: 'Machine Parts',
    sortPosition: 3,
    EK: [
      { commodityCode: '4711', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4711', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOULDING DEVICES',
    label: 'Molding Devices',
    EK: [
      { commodityCode: '4718', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4718', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY  FOR CONSTRUCTING, EXCAVATING, CONVEYING ',
    label: 'Machinery for constructing, excavating, conveying',
    EK: [
      { commodityCode: '4720', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4720', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS FR ROAD CONSTRUCTION MACHINERY,PARTS FOR GENERATORS',
    label: 'Parts from Road Construction Machinery, Parts for Generators',
    EK: [
      { commodityCode: '4741', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4741', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY FOR OIL DRILLING,OIL FIELD SERVICING,DREDGING ',
    label: 'Machinery for Oil Drilling, Oil Field Servicing, Dredging',
    EK: [
      { commodityCode: '4742', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4742', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINERY FR CONSTRUCTG,EARTH MOVG,CONVEYG,EXCAVATG,MINING ',
    label: 'Machinery for Constructing, Earth Moving, Conveying, Excavating',
    EK: [
      { commodityCode: '4747', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4747', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRILLING EQUIPMENT',
    label: 'Drilling Equipment',
    sortPosition: 10,
    EK: [
      { commodityCode: '4748', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4748', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MINING EQUIPMENT',
    label: 'Mining Equipment',
    sortPosition: 14,
    EK: [
      { commodityCode: '4750', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4750', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PIPES,PIPE FITTINGS,SPRINKLER SYSTEMS',
    label: 'Pipes, Pipe Fittings, Sprinkler',
    EK: [
      { commodityCode: '4753', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4753', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FILTERS, PUMPS',
    label: 'Filters, Pumps',
    sortPosition: 13,
    EK: [
      { commodityCode: '4754', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4754', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PARTS FOR DYE FILTERS',
    label: 'Parts for Dye Filters',
    EK: [
      { commodityCode: '4755', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4755', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC INJECTION MOULDING MACHINERY',
    label: 'Plastic Injection Moulding Machinery',
    EK: [
      { commodityCode: '4757', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4757', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TEXTILE/SEWING/SHOE MACHINERY',
    label: 'Textile, Sewing, and Shoe Machinery',
    EK: [
      { commodityCode: '4780', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4780', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEWING MACHINES  INCL ACCESSORIES, SUPPLIES ',
    label: 'Sewing Machines, including Accessories and Supplies',
    EK: [
      { commodityCode: '4789', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4789', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOOLS',
    label: 'Tools',
    EK: [
      { commodityCode: '4800', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4800', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TAPS AND DIES',
    label: 'Taps and Dies',
    EK: [
      { commodityCode: '4803', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4803', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MACHINE TOOLS',
    label: 'Machine Tools',
    sortPosition: 2,
    EK: [
      { commodityCode: '4811', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4811', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ABRASIVE TOOLS, CUTTING TOOLS',
    label: 'Abrasive Tools, Cutting Tools',
    EK: [
      { commodityCode: '4813', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4813', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TWIST/MASONRY DRILLS,MILLING CUTTERS,REAMERS,TOOL BITS,HACKS',
    label: 'Twist or Masonry Drills, Milling Cutters, Reamers, Tools',
    EK: [
      { commodityCode: '4816', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4816', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'UNIVERSAL FILLING GUIDES,GREASE GUNS,WOOD FILES,FIXEDSAWS HA',
    label: 'Universal Filling Guides, Grease Guns, Wood Files, Fixtures',
    EK: [
      { commodityCode: '4819', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4819', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PIPES, PIPE FITTINGS',
    label: 'Pipes, Pipe Fittings',
    EK: [
      { commodityCode: '4902', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4902', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BALL BEARINGS, ROLLER BEARINGS',
    label: 'Ball Bearings, Roller Bearings',
    EK: [
      { commodityCode: '4911', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4911', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PNEUMATIC EQUIPMENT, HYDRAULIC EQUIPMENT, TOOLS',
    label: 'Pneumatic Equipment, Hydraulic Equipment, Tools',
    EK: [
      { commodityCode: '4954', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4954', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMATIC DOOR CLOSERS',
    label: 'Automatic Door Closers',
    EK: [
      { commodityCode: '4955', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4955', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PISTONS, PISTON RINGS',
    label: 'Pistons, Piston Assemblies',
    EK: [
      { commodityCode: '4969', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '4969', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ABRASIVES,ABRASIVE PRODUCTS,GRINDING WHEELS',
    label: 'Abrasives, Abrasive Products, Grinding',
    EK: [
      { commodityCode: '5050', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5050', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SANITARY ARTICLES  MADE OF POTTERY,PORCELAIN ',
    label: 'Sanitary Articles Made of Pottery, Porcelain',
    EK: [
      { commodityCode: '5235', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5235', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'POTTERY, CERAMICS',
    label: 'Pottery, Ceramics',
    EK: [
      { commodityCode: '5295', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5295', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CERAMICS,POTTERY,CHINA/EARTHEN/STONE/CRYSTALWARE,PORCELAIN C',
    label: 'Ceramics, Pottery, China, Earthenware, Stoneware, Crystalware',
    EK: [
      { commodityCode: '5296', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5296', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'POTTERY,CHINA/EARTHEN/CRYSTALWARE,PORCELAIN WHOLLY IRISH MFR',
    label: 'Pottery, China, Earthenware, Crystalware, Porcelain Wholly',
    EK: [
      { commodityCode: '5297', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5297', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'POTTERY,CHINA/EARTHEN/STONE/CRYSTAL/GLASSWARE EXCL ARTIFCL S',
    label: 'Pottery, China, Earthenware, Stoneware, Crystal, Glassware, Excluding...',
    EK: [
      { commodityCode: '5299', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5299', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GLASS, GLASS PRODUCTS',
    label: 'Glass, Glass Products',
    EK: [
      { commodityCode: '5300', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5300', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GLASSWARE  INCL GLASS LAMPSHADES, ASSOCIATD ELEC FXTRS/FTNGS',
    label: 'Glassware, Including Glass Lampshades and Associated Electrical Components',
    EK: [
      { commodityCode: '5351', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5351', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MARBLE',
    label: 'Marble',
    EK: [
      { commodityCode: '5400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FIREPLACES  MARBLE ',
    label: 'Fireplaces, Marble',
    EK: [
      { commodityCode: '5406', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5406', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MICA, MICA PRODUCTS',
    label: 'Mica, Mica Products',
    EK: [
      { commodityCode: '5500', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5500', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OILS  REFINED ',
    label: 'Refined Oils',
    EK: [
      { commodityCode: '5620', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5620', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'QUARTZ',
    label: 'Quartz',
    EK: [
      { commodityCode: '5700', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5700', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AMETHYST,MALCHITE,MICA,SILICA,QUARTZ,SEMI-PRECIOUS STONS-RAW',
    label: 'Amethyst, Malachite, Mica, Silica, Quartz, Semi-Precious',
    EK: [
      { commodityCode: '5710', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5710', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'QUARTZ MANUFACTURES',
    label: 'Quartz Manufactures',
    EK: [
      { commodityCode: '5750', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5750', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'HANDICRAFT ENTIRELY OF ONYX',
    label: 'Handicraft Entirely of Onyx',
    EK: [
      { commodityCode: '5821', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5821', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEMI-PRECIOUS STONES',
    label: 'Semi-Precious Stones',
    EK: [{ commodityCode: '5851', product: 'GCR', productSHC: '', commoditySHC: '' }],
  },
  {
    value: 'ARTIFICIAL/GLASS STONES, GLASS/PLASTIC PEARS,UNSET,UNMOUNTED',
    label: 'Artificial and Glass Stones, Glass and Plastic Pearls, Unset',
    EK: [
      { commodityCode: '5858', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5858', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GRANITE SLABS',
    label: 'Granite Slabs',
    EK: [
      { commodityCode: '5900', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5900', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GEOLOGICAL SAMPLES  EXCLUDING ORE ',
    label: 'Geological Samples Excluding Ore',
    EK: [
      { commodityCode: '5901', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '5901', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEM/DYES/FERT/INSECTICIDES/PAINTS/DRUGS/COSMETICS/PLASTICS',
    label: 'Chemicals, Dyes, Fertilizers, Insecticides, Paints, Drugs, Cosmetics',
    EK: [
      { commodityCode: '6002', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6002', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHMCLS,DRUGS PARMA,MEDICINE EXCL COSMETICS,TOILET PREPS/ARTI',
    label: 'Chemicals, Drugs, Pharmaceuticals, Medicine excluding Cosmetics, Toiletries',
    EK: [
      { commodityCode: '6003', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6003', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DYES/FERT/INSECTICIDES/PAINTS/PIGMTS/COSMETICS/PLASTICS/OILS',
    label: 'Dyes, Fertilizers, Insecticides, Paints, Pigments, Cosmetics',
    EK: [
      { commodityCode: '6004', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6004', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DYES/CHEM/FERT/INSECTICIDES/PAINTS/OILS/PLASTICS',
    label: 'Dyes, Chemicals, Fertilizers, Insecticides, Paints, Oils',
    EK: [
      { commodityCode: '6005', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6005', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FOLL.PRODS.INCL.BRAND NMS,PRODS.OF ICI/SUBS/ASSO.CO.:ATROMID',
    label: 'Following Products, Including Brand Names, Products of ICI/Subsidiaries and Associate Companies',
    EK: [
      { commodityCode: '6006', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6006', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SURGICAL GAUZES/BANDAGES/COTTON WOOL/SANITARY TOWELS',
    label: 'Surgical Gauzes, Bandages, Cotton Wool, Sanitary',
    EK: [
      { commodityCode: '6007', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6007', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEM/DRUGS/PHARMA/MEDICINES/SURGICAL GAUZE/BANDAGES/PAINTS/V',
    label: 'Chemicals, Drugs, Pharmaceuticals, Medicines, Surgical Gauze, Bandages',
    EK: [
      { commodityCode: '6008', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6008', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEM/CHEM PDTS/DRUGS/COSMETCS/PHARMA/ESSENTL  OILS/PLASTIC',
    label: 'Chemicals, Chemical Products, Drugs, Cosmetics, Pharmaceuticals, Essential',
    EK: [
      { commodityCode: '6009', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6009', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'POLYPROPYLENE FILM METALISED /GRANULES',
    label: 'Polypropylene Film Metallized / Granules',
    EK: [
      { commodityCode: '6030', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6030', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ESSENTAIL OILS,PERFUMES,TOILET PREPARATION,SOAP,TOILET WATER',
    label: 'Essential Oils, Perfumes, Toilet Preparations, Soap, Toiletries',
    EK: [
      { commodityCode: '6050', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6050', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CHEMICALS',
    label: 'Chemicals',
    sortPosition: 11,
    EK: [
      { commodityCode: '6200', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOSQUITO COILS',
    label: 'Mosquito Coils',
    EK: [
      { commodityCode: '6202', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6202', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'XANTHOPHYLL CONCENTRATE',
    label: 'Xanthophyll Concentrate',
    EK: [
      { commodityCode: '6208', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6208', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DATURA EXTRACT',
    label: 'Datura Extract',
    EK: [
      { commodityCode: '6211', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6211', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRY COCHINEALS',
    label: 'Dry Cochineals',
    EK: [
      { commodityCode: '6212', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6212', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRY ICE',
    label: 'Dry Ice',
    EK: [
      { commodityCode: '6223', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6223', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INDL CHEMICALS, TESTING EQPT  MADE OF GLASS ',
    label: 'Industrial Chemicals, Testing Equipment Made of Glass',
    EK: [
      { commodityCode: '6224', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6224', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTING INK',
    label: 'Printing Ink',
    sortPosition: 17,
    EK: [
      { commodityCode: '6226', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6226', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PYRETHRUM EXTRACT',
    label: 'Pyrethrum Extract',
    EK: [
      { commodityCode: '6227', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6227', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ETHABUTOL POWDER',
    label: 'Ethambutol Powder',
    EK: [
      { commodityCode: '6234', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6234', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INSECTICIDE',
    label: 'Insecticide',
    EK: [
      { commodityCode: '6240', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6240', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VETERINARY MEDICINAL DIETARY ADDITIVE',
    label: 'Veterinary Medicinal Dietary Additive',
    EK: [
      { commodityCode: '6251', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6251', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'QUININE  ALKALOID ',
    label: 'Quinine Alkaloid',
    EK: [
      { commodityCode: '6271', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6271', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'GINSENG ROOT',
    label: 'Ginseng Root',
    EK: [
      { commodityCode: '6425', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6425', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'OPIUM',
    label: 'Opium',
    EK: [
      { commodityCode: '6435', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6435', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRUGS,MEDICINE,PHARMA.,INSECTICIDE,AIR FRESHNER AEROSOL CAN ',
    label: 'Drugs, Medicine, Pharmaceuticals, Insecticide, Air Freshener Aerosol',
    EK: [
      { commodityCode: '6495', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6495', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VETERINARY PHARMACEUTICALS',
    label: 'Veterinary Pharmaceuticals',
    EK: [
      { commodityCode: '6502', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6502', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY GELATINE CAPSULES FOR MEDICINES',
    label: 'Empty Gelatine Capsules for Medicines',
    EK: [
      { commodityCode: '6505', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6505', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BISMUTH, CADMIUM',
    label: 'Bismuth, Cadmium',
    EK: [
      { commodityCode: '6511', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6511', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COSMETICS,SOAPS,TOILET PREPARATIONS',
    label: 'Cosmetics, Soaps, Toilet Preparations',
    EK: [
      { commodityCode: '6600', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6600', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'COSMETICS,TOILET PREPARATIONS/ARTICLES, PERFUMES',
    label: 'Cosmetics, Toilet Preparations/Articles, Perfumes',
    EK: [
      { commodityCode: '6601', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6601', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TOILET ARTICLES/TOILET WATER/PERFUMES/COSMETICS-ALONE OR TOG',
    label: 'Toilet Articles, Toilet Water, Perfumes, Cosmetics, and Related Products',
    EK: [
      { commodityCode: '6605', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6605', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PERFUMES',
    label: 'Perfumes',
    EK: [
      { commodityCode: '6630', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6630', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PERFUME, TOILET WATER',
    label: 'Perfume, Toilet Water',
    EK: [
      { commodityCode: '6631', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6631', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAINTS/PIGMENTS/VARNISHES',
    label: 'Paints, Pigments, Varnishes',
    EK: [
      { commodityCode: '6750', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6750', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTING INK/LACQUERS, RUBBER STAMPS, INK PAD',
    label: 'Printing Ink/Lacquers, Rubber Stamps, Ink',
    EK: [
      { commodityCode: '6751', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6751', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FIBREGLASS, FIBREGLASS REINFORCING MATERIAL',
    label: 'Fiberglass, Fiberglass Reinforcing Material',
    EK: [
      { commodityCode: '6801', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6801', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ARTIFICIAL GUMS/RESINS, PLASTIC MANUFS. IN SHEETS, SLABS,ROD',
    label: 'Artificial Gums/Resins, Plastic Manufactures in Sheets',
    EK: [
      { commodityCode: '6805', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6805', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NATURAL RESINS',
    label: 'Natural Resins',
    EK: [
      { commodityCode: '6806', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6806', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC ARTICLES',
    label: 'Plastic Articles',
    sortPosition: 16,
    EK: [
      { commodityCode: '6810', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6810', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INSULATING TUBINGS',
    label: 'Insulating Tubings',
    EK: [
      { commodityCode: '6812', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6812', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC DOILIES',
    label: 'Plastic Doilies',
    EK: [
      { commodityCode: '6817', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6817', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC KITCHEN/TABLE/DECORATIVE WARE, TOYS',
    label: 'Plastic Kitchen/Table/Decorative Ware, Toys',
    EK: [
      { commodityCode: '6824', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6824', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC ARTICLES (FOR HOUSEHOLD USE) ',
    label: 'Plastic Articles for Household Use',
    EK: [
      { commodityCode: '6827', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6827', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC BLOOD BAGS, CELLOPHANE, POLYETHYLENE BAGS, ROLLS (FOR WRAPPING)',
    label: 'Plastic Blood Bags, Cellophane, Polyethylene Bags, Rolls (For Wrapping)',
    EK: [
      { commodityCode: '6847', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6847', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC CONTAINERS',
    label: 'Plastic Containers',
    EK: [
      { commodityCode: '6855', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6855', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLASTIC ARTICLES  USED IN ANIMAL HUSBANDRY ',
    label: 'Plastic Articles Used in Animal Husbandry',
    EK: [
      { commodityCode: '6857', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6857', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DETERGENTS,SOAPS,SOAP FLAKES/POWDER OTHER CLEANING MTRLS',
    label: 'Detergents, soaps, soap flakes or powder, and other cleaning supplies',
    EK: [
      { commodityCode: '6900', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6900', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'POLISHES,PASTE,POWDERS,PREPARATIONS-ISED FOR POLISHING,PRESE',
    label: 'Polishes, Pastes, Powders, and Preparations Used for Polishing',
    EK: [
      { commodityCode: '6901', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6901', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CLEANSING, POLISHING PRODUCTS',
    label: 'Cleansing, Polishing Products',
    EK: [
      { commodityCode: '6902', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '6902', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPER  SHEETS,ROLLS/EXCL PRNTD MATTER, PHOTOGRAPHIC PAPER ',
    label: 'Paper Sheets, Rolls (Excluding Printed Matter, Photographic Paper)',
    EK: [
      { commodityCode: '7001', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7001', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPER,CARDBOARD-IN SHEETS,ROLLS/EXCL PRNTD MATTER,PHOTO PAPE',
    label: 'Paper, cardboard in sheets, rolls, excluding printed matter',
    EK: [
      { commodityCode: '7003', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7003', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER, PAPER-INCL PHOTOGRAPHIC,SENSITIZED PAPER-EXC',
    label: 'Printed Matter, Paper - Including Photographic and Sensitized Paper - Excluding',
    EK: [
      { commodityCode: '7011', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7011', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPER PACKAGING MATERIAL',
    label: 'Paper Packaging Material',
    EK: [
      { commodityCode: '7021', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7021', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CIGARETTE PAPERS, FILTERS, TIPS, PACKAGING MATERIAL',
    label: 'Cigarette Papers, Filters, Tips, Packaging Material',
    EK: [
      { commodityCode: '7025', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7025', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SECURITY PAPER',
    label: 'Security Paper',
    EK: [
      { commodityCode: '7026', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7026', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SUFACE COATED PAPER  IN ROLLS, SHEETS ',
    label: 'Surface Coated Paper in Rolls, Sheets',
    EK: [
      { commodityCode: '7031', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7031', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CABLE INSULATION PAPER TAPE',
    label: 'Cable Insulation Paper Tape',
    EK: [
      { commodityCode: '7032', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7032', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADHESIVE TAPE, ADHESIVE TAPE DISPENSERS',
    label: 'Adhesive Tape, Adhesive Tape Dispensers',
    EK: [
      { commodityCode: '7039', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7039', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHOTOGRAPHIC SENSITIZED PAPER',
    label: 'Photographic Sensitized Paper',
    EK: [
      { commodityCode: '7046', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7046', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CARDS/CHECKS/TKTS/TKTROLLS/LBLS OF PAPER,METAL,PLASTIC ',
    label: 'Cards, Checks, Tickets, Ticket Rolls, Labels of Paper',
    EK: [
      { commodityCode: '7067', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7067', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'STATIONERY',
    label: 'Stationery',
    EK: [
      { commodityCode: '7090', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7090', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTER MATTER',
    label: 'Printed Matter',
    EK: [
      { commodityCode: '7100', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7100', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NWP.,MAGS,PERIODICALS,BOOKS,PRINTED MATTER EXCL.POSTAGE STAM',
    label: 'Newspapers, Magazines, Periodicals, Books, Printed Matter Excluding...',
    EK: [
      { commodityCode: '7101', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7101', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPPR/MAGAZINES/PERIODCLS EXCL STAMPS,SHARECPNS,PRESS PHOT',
    label: 'Newspapers, Magazines, Periodicals excluding Stamps, Share Coupons',
    EK: [
      { commodityCode: '7102', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7102', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPERS, MAGAZINES',
    label: 'Newspapers, Magazines',
    EK: [
      { commodityCode: '7104', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7104', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BOOKS CATALOGUES, PAMPHLETS',
    label: 'Books, Catalogues, Pamphlets',
    EK: [
      { commodityCode: '7105', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7105', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MAGAZINES,PERIODCALS PUBLISHED ONCE WEEKLY OR LONGER INTERVALS - BOOKS EXCLUDING PHOTOS',
    label: 'Magazines, Periodicals Published Once Weekly or Longer Intervals - Books Excluding Photos',
    EK: [
      { commodityCode: '7106', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7106', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPERS  DAILY ',
    label: 'Newspapers, Daily',
    EK: [
      { commodityCode: '7107', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7107', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ADMINISTRATIVE REPORTS',
    label: 'Administrative Reports',
    EK: [
      { commodityCode: '7108', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7108', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPERS',
    label: 'Newspapers',
    EK: [
      { commodityCode: '7109', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7109', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPERS,MAGAZINES,PERIODICALS     ',
    label: 'Newspapers, Magazines, Periodicals',
    EK: [
      { commodityCode: '7110', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7110', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FOOTBALL POOL COUPONS',
    label: 'Football Pool Coupons',
    EK: [
      { commodityCode: '7111', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7111', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPEPRS,MAGAZINES,PERIODICALS,BOOKS',
    label: 'Newspapers, Magazines, Periodicals',
    EK: [
      { commodityCode: '7112', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7112', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MAGAZINES, PERIODICALS, NEWSPAPERS - PUBLISHED ONCE WEEKLY OR AT LONGER INTERVALS',
    label: 'Magazines, Periodicals, Newspapers - Published Once Weekly or at Longer Intervals',
    EK: [
      { commodityCode: '7113', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7113', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BOOKS, MAGAZINES, CATALOGUES',
    label: 'Books, Magazines, Catalogues',
    EK: [
      { commodityCode: '7115', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7115', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MAGS, PERIODICALS EXCLUDING NEWSPAPERS',
    label: 'Magazines, Periodicals Excluding Newspapers',
    EK: [
      { commodityCode: '7117', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7117', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWP/MAG/PERS/BOOKS',
    label: 'Newspapers, Magazines, Periodicals',
    EK: [
      { commodityCode: '7118', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7118', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BOOKS        ',
    label: 'Books',
    EK: [
      { commodityCode: '7119', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7119', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPERS AND MAGAZINES',
    label: 'Newspapers and Magazines',
    EK: [
      { commodityCode: '7120', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7120', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER  EXCL NEWSPAPERS ',
    label: 'Printed Matter, Excluding Newspapers',
    EK: [
      { commodityCode: '7122', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7122', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RELIGIOUS BOOKS',
    label: 'Religious Books',
    EK: [
      { commodityCode: '7123', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7123', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PRINTED MATTER........',
    label: 'Printed Matter',
    EK: [
      { commodityCode: '7126', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7127', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7128', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7126', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '7127', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
      { commodityCode: '7128', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'NEWSPAPER,MAGS,PERIODICALS,BOOKS PRNTD MATTER,PHOTOGRAPHS',
    label: 'Newspaper, Magazines, Periodicals, Books, Printed Matter, etc.',
    EK: [
      { commodityCode: '7129', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7129', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'TEXTBOOKS,INSTRUMTS,VISUAL TEACHG AIDS-FOR USE EDUCATNL INST',
    label: 'Textbooks, Instruments, Visual Teaching Aids - For Use Educational Institutions',
    EK: [
      { commodityCode: '7159', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7159', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '***** RECORD COVERS',
    label: 'Record Covers',
    EK: [
      { commodityCode: '7163', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7163', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LABELS ,CARBOARD CARTONS UNASSEMBLED ,OFC STATIONERY,PROMOTIO',
    label: 'Labels, Cardboard Cartons Unassembled, Office Stationery',
    EK: [
      { commodityCode: '7178', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7178', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SELF-ADHESIVE LABELS',
    label: 'Self-Adhesive Labels',
    EK: [
      { commodityCode: '7179', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7179', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LABELS,ADHESIVE TAPE,DISPENSER,CARDBOARD CARTON UNASSEMBLED ',
    label: 'Labels, Adhesive Tape, Dispenser, Cardboard Carton',
    EK: [
      { commodityCode: '7181', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7181', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WILLOW ARTICLES  WOVEN ',
    label: 'Willow Articles Woven',
    EK: [
      { commodityCode: '7381', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7381', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'BASKETWARE',
    label: 'Basketware',
    EK: [
      { commodityCode: '7382', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7382', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER, RUBBER PRODUCTS',
    label: 'Rubber, Rubber Products',
    EK: [
      { commodityCode: '7400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ROLLS,SHEETS,SLABS,UNFINISHED FORMS-MADE OF RUBBER',
    label: 'Rolls, Sheets, Slabs, Unfinished Forms - Made',
    EK: [
      { commodityCode: '7415', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7415', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER GOODS',
    label: 'Rubber Goods',
    sortPosition: 18,
    EK: [
      { commodityCode: '7420', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7420', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER BELTS,RUBBER BELTING',
    label: 'Rubber Belts, Rubber Belting',
    EK: [
      { commodityCode: '7425', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7425', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER BOOTS',
    label: 'Rubber Boots',
    EK: [
      { commodityCode: '7437', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7437', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER SEALS',
    label: 'Rubber Seals',
    EK: [
      { commodityCode: '7448', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7448', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RUBBER TYRES, RUBBER TUBES',
    label: 'Rubber Tires, Rubber Tubes',
    EK: [
      { commodityCode: '7481', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7481', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SEALING RINGS  MADE OF ARTIFICIAL RUBBER ',
    label: 'Sealing Rings Made of Artificial Rubber',
    EK: [
      { commodityCode: '7483', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7483', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'STRAW PRODUCTS, GRASS PRODUCTS',
    label: 'Straw Products, Grass Products',
    EK: [
      { commodityCode: '7500', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7500', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOOD, WOOD PRODUCTS',
    label: 'Wood, Wood Products',
    EK: [
      { commodityCode: '7600', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7600', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PLYWOOD, VENEERWOOD',
    label: 'Plywood, Veneers, Etc.',
    EK: [
      { commodityCode: '7601', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7601', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOOD PARQUET FLOORING',
    label: 'Wood Parquet Flooring',
    EK: [
      { commodityCode: '7608', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7608', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '*****WOOD MANUFACTURES',
    label: 'Wood Manufactures',
    EK: [
      { commodityCode: '7620', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7620', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FURNITURE,FURNITURE COMPONENTS,ARTIFACTS,CURIOS-MADE OF WOOD',
    label: 'Furniture, Furniture Components, Artifacts, Curios - Made of Wood',
    EK: [
      { commodityCode: '7625', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7625', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOODEN FURNITURE  EXCL OBJECTS OF ART ',
    label: 'Wooden Furniture Excluding Objects of Art',
    EK: [
      { commodityCode: '7627', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7627', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FURNITURE  EXCL OBJECTS OF ART ',
    label: 'Furniture excluding objects of art',
    EK: [
      { commodityCode: '7628', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7628', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: '***** WOODEN FURNITURE  NOT ASSMBLED/EXCL OBJECTS OF ART ',
    label: 'Wooden Furniture Not Assembled, Excluding Objects',
    EK: [
      { commodityCode: '7629', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7629', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WOODEN CABINETS FOR RADIOS,TVS,PHONOGRAPHS,SEWING MACHINES ',
    label: 'Wooden Cabinets for Radios, Televisions, Phonographs, Sewing',
    EK: [
      { commodityCode: '7630', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '7630', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SCIENTIFIC/PRECISION INSTRUMENTS EXCL WATCHES/CLOCKS',
    label: 'Scientific/Precision Instruments excluding Watches/Clocks',
    EK: [
      { commodityCode: '8001', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8001', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SCIENTIFIC/PRECISION/DENTAL/SURGICAL INST.,WATCHES,CLOCKS',
    label: 'Scientific, Precision, Dental, Surgical Instruments, Watches, Clocks',
    EK: [
      { commodityCode: '8002', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8002', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DENTAL/SURGICAL/MEDICAL EQPT',
    label: 'Dental/Surgical/Medical Equipment',
    EK: [
      { commodityCode: '8100', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8100', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'DRAIN TUBES, PLASTIC ARTICLES, RUBBER ARTICLES - FOR HOSPITAL USE -',
    label: 'Drain Tubes, Plastic Articles, Rubber Articles - For Hospital Use',
    EK: [
      { commodityCode: '8167', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8167', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MUSICAL INST,PHONOGRAPHS,PHONOGRAPH RECORDS,CASSETTE TAPES',
    label: 'Musical Instruments, Phonographs, Phonograph Records, Cassette Tapes',
    EK: [
      { commodityCode: '8200', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8200', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MUSICL INSTRMTS,MUSIC BOX,PHONOGRAPHS EXCL COMB WITH CLOCKS',
    label: 'Musical Instruments, Music Box, Phonographs, excluding combined with clocks.',
    EK: [
      { commodityCode: '8206', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8206', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MUSICL INSTRMTS,PHONOGRAPH RCRDS,TAPE RECORDGS,SHEET MUSIC',
    label: 'Musical Instruments, Phonograph Records, Tape Recordings, Sheet Music',
    EK: [
      { commodityCode: '8211', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8211', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RECORDS, RECORDING TAPE, RECORDING WIRE',
    label: 'Records, Recording Tape, Recording Wire',
    EK: [
      { commodityCode: '8277', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8277', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHONOGRAPH RECORDS,RECORDING TAPE',
    label: 'Phonograph Records, Recording Tape',
    EK: [
      { commodityCode: '8280', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8280', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'RECORDING TAPES,CASSETTES,PHONOGRAPH RECORDS,FILMS',
    label: 'Recording Tapes, Cassettes, Phonograph Records, Films',
    EK: [
      { commodityCode: '8281', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8281', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHONOGRAPH RECORDS',
    label: 'Phonograph Records',
    EK: [
      { commodityCode: '8282', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8282', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'VIDEO CASSETTES',
    label: 'Video Cassettes',
    EK: [
      { commodityCode: '8284', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8284', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CASSETTE TAPES',
    label: 'Cassette Tapes',
    EK: [
      { commodityCode: '8285', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8285', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CONTACT LENSES',
    label: 'Contact Lenses',
    EK: [
      { commodityCode: '8303', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8303', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LENSES, PRISMS',
    label: 'Lenses, Prisms',
    EK: [
      { commodityCode: '8340', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8340', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LENSES FRAMES, SUNGLASSES',
    label: 'Lenses, Frames, Sunglasses',
    EK: [
      { commodityCode: '8370', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8370', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SPECTACLE FRAMES',
    label: 'Spectacle Frames',
    EK: [
      { commodityCode: '8371', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8371', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SPECTACLES,SUNGLASSES - INCL ACCESSORIES',
    label: 'Spectacles, Sunglasses - Including Accessories',
    EK: [
      { commodityCode: '8372', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8372', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SUNGLASSES,GOGGLES',
    label: 'Sunglasses, Goggles',
    EK: [
      { commodityCode: '8380', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8380', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'SUNGLASSES,GOGGLES-INCL SPECTACLE CASES WHEN SHPD WITH MAIN',
    label: 'Sunglasses, Goggles - Including Spectacle Cases When Shipped With',
    EK: [
      { commodityCode: '8382', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8382', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES, ACCESSORI',
    label: 'Cameras, optical goods, photographic equipment, projection equipment including supplies, and accessories.',
    EK: [
      { commodityCode: '8391', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8391', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES -EXCLUDIN',
    label: 'Cameras, optical goods, photographic equipment, projection equipment - including supplies - excluding',
    EK: [
      { commodityCode: '8392', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8392', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES - FILMS EX',
    label:
      'Cameras, Optical Goods, Photographic Equipment, Projection Equipment - Including Supplies - Films Excluding',
    EK: [
      { commodityCode: '8393', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8393', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PROJN EQPT INCL SUPP/ACCS EXCL NEWSREEL,35MM FILM EXPOSED',
    label: 'Projection Equipment, Including Supplies and Accessories, Excluding Newsreel',
    EK: [
      { commodityCode: '8395', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8395', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, SPECTACLES, BINOCULARS, MICROSCOPES, TELESCOPES, PHOTOGRAPHIC INSTRUCTIONS, PROJECTION INST',
    label: 'Cameras, Spectacles, Binoculars, Microscopes, Telescopes, Photographic Instruments, Projection',
    EK: [
      { commodityCode: '8396', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8396', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, OPTICAL GOODS, PHOTOGRAPHIC EQUIPMENT, PROJECTION EQUIPMENT - INCLUDING SUPPLIES -, PUBLICI',
    label: 'Cameras, Optical Goods, Photographic Equipment, Projection Equipment - Including Supplies -, Publications',
    EK: [
      { commodityCode: '8398', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8398', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, PHOTOGRAPHIC INSTRUMENTS, PROJECTION INSTRUMENTS - INCLUDING APPARATUS, SUPPLIES, ACCESSORI',
    label: 'Cameras, Photographic Instruments, Projection Instruments - Including Apparatus, Supplies, Accessories',
    EK: [
      { commodityCode: '8400', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8400', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CAMERAS, PHOTOGRAPHIC EQUIPMENT (INCLUDING ACCESSORIES)',
    label: 'Cameras, Photographic Equipment (Including Accessories)',
    EK: [
      { commodityCode: '8405', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8405', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FILMS',
    label: 'Films',
    EK: [
      { commodityCode: '8420', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8420', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'FILMS  UNEXPOSED , SENSITIVE PAPER',
    label: 'Films, Unexposed, Sensitive Paper',
    EK: [
      { commodityCode: '8423', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8423', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'ENTERTAINMENT PARTS',
    label: 'Entertainment Parts',
    EK: [
      { commodityCode: '8424', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8424', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOTION PICTURE FILMS',
    label: 'Motion Picture Films',
    EK: [
      { commodityCode: '8425', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8425', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'MOTION PICTURE FILMS (INCL ADVTG MTRLS)      ',
    label: 'Motion Picture Films (Including Advertising Materials)',
    EK: [
      { commodityCode: '8426', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8426', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'PHOTO FILMS/PAPER-SENSITIZED,UNEXPSD/INCL PHOTO CHMCS,UNSENS',
    label: 'Photo Films/Paper-Sensitized, Unexposed, Including Photo',
    EK: [
      { commodityCode: '8430', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8430', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'UNEXPOSED FILMS, UNRECORDED TAPES',
    label: 'Unexposed Films, Unrecorded Tapes',
    EK: [
      { commodityCode: '8434', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8434', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'LENSES',
    label: 'Lenses',
    EK: [
      { commodityCode: '8440', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8440', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'AUTOMATIC CONTROL FOR MEASURG/REGULATG FLOW TEMP,HUMIDITY,TI',
    label: 'Automatic control for measuring or regulating flow temperature, humidity, and time',
    EK: [
      { commodityCode: '8551', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8551', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'INSTRUMENTS FOR REGULATING, MEASURING, TESTING',
    label: 'Instruments for Regulating, Measuring, Testing',
    EK: [
      { commodityCode: '8909', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8909', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WATCHES, CLOCKS',
    label: 'Watches, Clocks',
    EK: [
      { commodityCode: '8911', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8911', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WRIST WATCHES  WITH BAND OR BRACELETS ',
    label: 'Wristwatches with Band or Bracelet',
    EK: [
      { commodityCode: '8913', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8913', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'WATCHES',
    label: 'Watches',
    EK: [
      { commodityCode: '8925', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '8925', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'IMITATION JEWELRY,PERSONAL ORNAMENTS,ARTIFICIAL/GLASS STONES',
    label: 'Imitation Jewelry, Personal Ornaments, Artificial/Glass Stones',
    EK: [
      { commodityCode: '9001', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9001', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'IMITATION JEWELRY,NOVELTIES,ORNAMTS EXCL WATCHES,CLOCKS',
    label: 'Imitation Jewelry, Novelties, Ornaments excluding Watches.',
    EK: [
      { commodityCode: '9006', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9006', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY CONTAINERS',
    label: 'Empty Containers',
    EK: [
      { commodityCode: '9050', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9050', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY BEER KEGS',
    label: 'Empty Beer Kegs',
    EK: [
      { commodityCode: '9051', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9051', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY AMMONIA CYLINDERS',
    label: 'Empty Ammonia Cylinders',
    EK: [
      { commodityCode: '9052', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9052', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY BOXES OF CARDBOARD,NON-PRECIOUS METAL/FOR JEWELRY,WATC',
    label: 'Empty Boxes of Cardboard, Non-Precious Metal/for Jewelry',
    EK: [
      { commodityCode: '9072', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9072', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'EMPTY METAL CANS',
    label: 'Empty Metal Cans',
    EK: [
      { commodityCode: '9078', product: 'GCR', productSHC: '', commoditySHC: '' },
      { commodityCode: '9078', product: 'AXA', productSHC: 'AXA', commoditySHC: '' },
    ],
  },
  {
    value: 'CUT FLOWERS  EXCL ORCHIDS ',
    label: 'Cut Flowers excluding Orchids',
    sortPosition: 6,
    EK: [
      { commodityCode: '1430', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1430', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CARNATION CUTTINGS, CHRYSANTHEMUM CUTTINGS, GLADIOLI',
    label: 'Carnation Cuttings, Chrysanthemum Cuttings, Gladioli Cuttings, and Other Live Plants',
    EK: [
      { commodityCode: '1436', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1436', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FERN',
    label: 'Fern',
    EK: [
      { commodityCode: '1471', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1471', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'YUCA CUTTINGS',
    label: 'Yuca Cuttings',
    EK: [
      { commodityCode: '1472', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1472', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PLANTS',
    label: 'Plants',
    sortPosition: 3,
    EK: [
      { commodityCode: '1475', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1475', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PLANTS  EXCL. ORCHIDS ',
    label: 'Plants excluding Orchids',
    EK: [
      { commodityCode: '1476', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1476', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TROPICAL PLANTS  EXCL CUT FLOWERS ',
    label: 'Tropical Plants, Excluding Cut Flowers',
    EK: [
      { commodityCode: '1477', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1477', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SEEDS, PLANTS',
    label: 'Seeds, Plants',
    EK: [
      { commodityCode: '1479', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1479', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TROPICAL PLANTS, SEEDS',
    label: 'Tropical Plants, Seeds',
    EK: [
      { commodityCode: '1480', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1480', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLOWERS, PLANTS',
    label: 'Flowers, Plants',
    EK: [
      { commodityCode: '1499', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1499', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HERBS, LEAVES - INCLUDING PRODUCTS THEREOF -',
    label: 'Herbs, Leaves - Including Products Thereof',
    EK: [
      { commodityCode: '1500', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1500', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HENNA  DRIED ',
    label: 'Henna, Dried',
    EK: [
      { commodityCode: '1503', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1503', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****PEPPER STEMS',
    label: 'Pepper Stems',
    EK: [
      { commodityCode: '1526', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1526', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BETEL LEAVES',
    label: 'Betel Leaves',
    EK: [
      { commodityCode: '1535', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '1535', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLORAL,NURSERY STOCK,BULBS,SEEDS,PLANTS,FLOWERS',
    label: 'Floral, Nursery Stock, Bulbs, Seeds, Plants',
    EK: [
      { commodityCode: '9702', product: 'PXD', productSHC: '', commoditySHC: '' },
      { commodityCode: '9702', product: 'PXS', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SALMON',
    label: 'Salmon',
    EK: [
      { commodityCode: '0340', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0340', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SNAPPER',
    label: 'Snapper',
    EK: [
      { commodityCode: '0355', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0355', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FISH,CRABS,SHRIMPS-EDIBLE',
    label: 'Fish, Crabs, Shrimps - Edible',
    EK: [
      { commodityCode: '0356', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0356', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SHARK FINS',
    label: 'SHARK FINS',
    EK: [
      { commodityCode: '0370', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0370', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SHRIMPS, PRAWNS',
    label: 'Shrimps, etc.',
    EK: [
      { commodityCode: '0380', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0380', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****SHRIMPS AND/OR PRAWNS',
    label: 'Shrimps and/or',
    EK: [
      { commodityCode: '0382', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0382', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SHRIMPS, PRAWNS, LOBSTERS',
    label: 'Shrimps, Prawns',
    EK: [
      { commodityCode: '0384', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0384', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LOBSTERS',
    label: 'Lobsters',
    EK: [
      { commodityCode: '0386', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0386', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BERRIES, MELONS',
    label: 'Berries, Melons',
    EK: [
      { commodityCode: '0400', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0400', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BERRIES',
    label: 'Berries',
    EK: [
      { commodityCode: '0401', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0401', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUIT  EXCLUDING MELONS ',
    label: 'Fruit Excluding Melons',
    EK: [
      { commodityCode: '0410', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0410', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUIT  FRESH ',
    label: 'Fruit, Fresh',
    EK: [
      { commodityCode: '0413', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0413', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BANANAS',
    label: 'Bananas',
    EK: [
      { commodityCode: '0420', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0420', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LIMES, LEMONS',
    label: 'Limes, Lemons',
    EK: [
      { commodityCode: '0422', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0422', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'GRAPES',
    label: 'Grapes',
    sortPosition: 8,
    EK: [
      { commodityCode: '0445', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0445', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'KIWIFRUIT',
    label: 'Kiwifruit',
    EK: [
      { commodityCode: '0456', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0456', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MANGOES',
    label: 'Mangoes',
    EK: [
      { commodityCode: '0460', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0460', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'DATES',
    label: 'Dates',
    EK: [
      { commodityCode: '0461', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0461', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'OLIVES',
    label: 'Olives',
    EK: [
      { commodityCode: '0462', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0462', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CHERRIES',
    label: 'Cherries',
    sortPosition: 7,
    EK: [
      { commodityCode: '0477', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0477', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PINEAPPLES',
    label: 'Pineapples',
    EK: [
      { commodityCode: '0485', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0485', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPAYAS',
    label: 'Papayas',
    EK: [
      { commodityCode: '0486', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0486', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PAPAYAS, PINEAPPLES',
    label: 'Papayas, Pineapples',
    EK: [
      { commodityCode: '0487', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0487', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'RASPBERRIES',
    label: 'Raspberries',
    EK: [
      { commodityCode: '0494', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0494', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'STRAWBERRIES',
    label: 'Strawberries',
    EK: [
      { commodityCode: '0495', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0495', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'INDIAN SWEETMEATS',
    label: 'Indian Sweetmeats',
    EK: [
      { commodityCode: '0497', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0497', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLOUR',
    label: 'Flour',
    EK: [
      { commodityCode: '0509', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0509', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BAKERY PRODUCTS',
    label: 'Bakery Products',
    EK: [
      { commodityCode: '0510', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0510', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PASTRY DOUGH',
    label: 'Pastry Dough',
    EK: [
      { commodityCode: '0514', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0514', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BREAD',
    label: 'Bread',
    EK: [
      { commodityCode: '0515', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0515', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FRUIT CAKES',
    label: 'Fruit Cakes',
    EK: [
      { commodityCode: '0521', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0521', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CEREAL FOODS  READY TO EAT ',
    label: 'Cereal Foods Ready to Eat',
    EK: [
      { commodityCode: '0552', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0552', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MEAT INCLUDING SLAUGHTERED POULTRY',
    label: 'Meat including slaughtered poultry',
    EK: [
      { commodityCode: '0600', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0600', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MEAT  FRESH OR FROZEN ',
    label: 'Meat, Fresh or Frozen',
    sortPosition: 9,
    EK: [
      { commodityCode: '0601', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0601', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MEAT, SAUSAGES, BOLOGNA, FRANKFURTERS',
    label: 'Meat, Sausages, Bologna, Frankfurters',
    EK: [
      { commodityCode: '0602', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0602', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MEAT, SEAFOOD',
    label: 'Meat, Seafood',
    EK: [
      { commodityCode: '0603', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0603', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MEAT  EXCL SLAUGHTERED POULTRY, GAME ',
    label: 'Meat Excluding Slaughtered Poultry',
    EK: [
      { commodityCode: '0605', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0605', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PATE DE FOIE GRAS',
    label: 'Pâté de Foie Gras',
    EK: [
      { commodityCode: '0606', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0606', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LAMB, MUTTON',
    label: 'Lamb, Mutton, and Sheep',
    EK: [
      { commodityCode: '0630', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0630', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'POULTRY, GAME',
    label: 'Poultry, Game',
    EK: [
      { commodityCode: '0640', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0640', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LIVER  RAW ',
    label: 'Raw Liver',
    EK: [
      { commodityCode: '0645', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0645', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****MEAT FRESH-FROZEDN INCL HORSE MEAT AND POULTRY',
    label: 'Meat Fresh - Frozen, Including Horse Meat and Parts',
    EK: [
      { commodityCode: '0650', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0650', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'PORK, CHICKEN - CHILLED OR FROZEN',
    label: 'Pork, Chicken - Chilled or Frozen',
    EK: [
      { commodityCode: '0651', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0651', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BACON',
    label: 'Bacon',
    EK: [
      { commodityCode: '0655', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0655', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HORSE MEAT',
    label: 'Horse Meat',
    EK: [
      { commodityCode: '0670', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0670', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ROOTS AND SPICES',
    label: 'Roots and Spices',
    EK: [
      { commodityCode: '0700', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0700', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SPICES, SPICE EXTRACTS EXCLUDING SAFFRON',
    label: 'Spices, Spice Extracts Excluding Saffron',
    EK: [
      { commodityCode: '0702', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0702', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CARDAMOM',
    label: 'Cardamom',
    EK: [
      { commodityCode: '0728', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0728', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BIRDEYE CHILLIES  DRIED ',
    label: 'Birdseye Chillies',
    EK: [
      { commodityCode: '0750', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0750', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'GINGER',
    label: 'Ginger',
    EK: [
      { commodityCode: '0751', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0751', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'VEGETABLES',
    label: 'Vegetables',
    sortPosition: 1,
    EK: [
      { commodityCode: '0800', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0800', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ARTICHOKES',
    label: 'Artichokes',
    EK: [
      { commodityCode: '0801', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0801', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BROCCOLI',
    label: 'Broccoli',
    EK: [
      { commodityCode: '0802', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0802', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'LETTUCE',
    label: 'Lettuce',
    EK: [
      { commodityCode: '0803', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0803', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ASPARAGUS',
    label: 'Asparagus, Maracuya',
    EK: [
      { commodityCode: '0805', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0805', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'YAMS, COCOYAMS, PLAINTAINS',
    label: 'Yams, Cocoyams',
    EK: [
      { commodityCode: '0807', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0807', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BEANS',
    label: 'Beans',
    EK: [
      { commodityCode: '0810', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0810', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ENDIVES, CELERY',
    label: 'Endives, Celery',
    EK: [
      { commodityCode: '0838', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0838', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'MUSHROOMS, MUSHROOM SPAWN',
    label: 'Mushrooms, Mushroom Products',
    EK: [
      { commodityCode: '0850', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0850', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TAROS',
    label: 'Taro',
    EK: [
      { commodityCode: '0866', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0866', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TOMATOES',
    label: 'Tomatoes',
    EK: [
      { commodityCode: '0880', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0880', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'SOUPS',
    label: 'Soups',
    EK: [
      { commodityCode: '0885', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0885', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****YEAST - INACTIVE',
    label: 'Yeast - Inedible',
    EK: [
      { commodityCode: '0898', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0898', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'YEAST',
    label: 'Yeast',
    EK: [
      { commodityCode: '0899', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0899', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'COOKING FAT',
    label: 'Cooking Fat',
    EK: [
      { commodityCode: '0901', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '0901', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'HATCHING EGGS',
    label: 'Hatching Eggs',
    EK: [
      { commodityCode: '1082', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1082', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'TURKEY HATCHING EGGS',
    label: 'Turkey Hatching Eggs',
    EK: [
      { commodityCode: '1084', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1084', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'GUM ARABIC',
    label: 'Gum Arabic',
    EK: [
      { commodityCode: '1352', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1352', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****FRANKINCESE AND GUMS',
    label: 'Frankincense and',
    EK: [
      { commodityCode: '1353', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1353', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLORAL/NURSERY STOCK,BULBS,FLOWERS,SEEDS,TUBERS',
    label: 'Floral/Nursery Stock, Bulbs, Flowers, Seeds',
    EK: [
      { commodityCode: '1400', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1400', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLORAL/NURSERY STOCK,BULBS,SEEDS,PLANTS,FLOWERS',
    label: 'Floral Nursery Stock, Bulbs, Seeds',
    EK: [
      { commodityCode: '1401', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1401', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLORAL/NURSERY STOCK EXCL CUT FLOWERS ,BULBS,SEEDS,TUBERS',
    label: 'Floral/Nursery Stock Excluding Cut Flowers, Bulbs, Seeds',
    EK: [
      { commodityCode: '1402', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1402', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'CUT FLOWERS, FOLIAGE, CUTTINGS',
    label: 'Cut Flowers, Foliage, Cuttings',
    EK: [
      { commodityCode: '1403', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1403', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'BULBS, TUBERS',
    label: 'Bulbs',
    EK: [
      { commodityCode: '1409', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1409', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ROSE PETALS',
    label: 'Rose Petals',
    EK: [
      { commodityCode: '1411', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1411', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'FLOWERS',
    label: 'Flowers',
    EK: [
      { commodityCode: '1420', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1420', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: '*****CUT FLOWERS,FOLIAGE, INCL MIMOSA',
    label: 'Cut Flowers, Foliage, Including',
    EK: [
      { commodityCode: '1421', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1421', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'ORCHIDS',
    label: 'Orchids',
    EK: [
      { commodityCode: '1429', product: 'PXS', productSHC: '', commoditySHC: '' },
      { commodityCode: '1429', product: 'PXD', productSHC: '', commoditySHC: '' },
    ],
  },
  {
    value: 'RADIOACTIVE MTRLS  FOR MEDICAL/LABORATORY USE ',
    label: 'Radioactive Materials for Medical or Laboratory Use',
    EK: [{ commodityCode: '6012', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'PLASTIC SURGICAL EQUIPMENT, HUMAN PLASMA',
    label: 'Plastic Surgical Equipment, Human Plasma',
    EK: [{ commodityCode: '6110', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'CAMERAS, INSTRUMENTS FOR DENTISTRY, SURGERY, MEASURING, CALIBRATING, TESTING, DRAWING, ANALYSING SCA',
    label: 'Cameras, instruments for dentistry, surgery, measuring, calibrating, testing, drawing, analyzing.',
    EK: [{ commodityCode: '8549', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SAFETY EQPT AND APPARATUS,GENERIC ITEM OF DENTAL/MEDI/OPTICAL',
    label: 'Safety Equipment and Apparatus, Generic Item of Dental, Medical, Optical',
    EK: [{ commodityCode: '8550', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'ELECTRO MEDICAL APPARATUS/OPER.TABLES/DIATHERMIC MACHNS/DENT',
    label: 'Electro-Medical Apparatus, Operating Tables, Diathermic Machines',
    EK: [{ commodityCode: '8553', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'MEDICAL EQUIPMENT  INCLUDING SUPPLIES ',
    label: 'Medical Equipment, Including Supplies',
    EK: [{ commodityCode: '8575', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SCIENTIFIC/LAB INSTRMTS INCL APPARATUS, SUPPLIES',
    label: 'Scientific/Lab Instruments including Apparatus, Supplies',
    EK: [{ commodityCode: '8600', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SCIENTIFIC/LAB INSTRMTS INCL APPARATUS',
    label: 'Scientific/Laboratory Instruments Including Apparatus',
    EK: [{ commodityCode: '8601', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'ELECTRIC MEDICAL EQUIPMENT',
    label: 'Electric Medical Equipment',
    EK: [{ commodityCode: '8603', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'ANALYTICAL MEASURING INSTRMTS FOR MED/SCIENTIFIC PURPOSES ',
    label: 'Analytical Measuring Instruments for Medical/Scientific Purposes',
    EK: [{ commodityCode: '8610', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'HUMAN EYES,TISSUES,DEHYDRATED CORNEAS',
    label: 'Human Eyes, Tissues, Dehydrated',
    EK: [{ commodityCode: '9092', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'INSTRUMENTS FOR DENTISTRY, SURGERY, MEASURING, CALIBRATING, X-RAY EQUIPMENT, ANALYSING SCALES, MICRO',
    label: 'Instruments for dentistry, surgery, measuring, calibrating, X-ray equipment, analyzing scales',
    EK: [{ commodityCode: '8003', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SURGICAL BEDS',
    label: 'Surgical Beds',
    EK: [{ commodityCode: '8103', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'DENTAL/SURGCL/MEDICL INSTRMTS INCL APPARATUS,SUPPLIES',
    label: 'Dental/Surgical/Medical Instruments, Including Apparatus',
    EK: [{ commodityCode: '8105', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'INJECTION INSTRUMENTS, INFUSION SETS',
    label: 'Injection Instruments, Infusion Sets',
    EK: [{ commodityCode: '8112', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'HYPODERMIC (SURGICAL) NEEDLES',
    label: 'Hypodermic (Surgical)',
    EK: [{ commodityCode: '8114', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'ARTIFICIAL TEETH,RAW MTRL FOR ARTFCL TEETH, MFD FELDSPAR',
    label: 'Artificial Teeth, Raw Materials for Artificial Teeth',
    EK: [{ commodityCode: '8181', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'PROSTHESES',
    label: 'Leather, Leather Products Excluding Sporting Goods',
    EK: [{ commodityCode: '8183', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'SCI/PRECISION INSTRMTS,LAB EQPT(INCL SUPPL), WATCHES, CLOCKS',
    label: 'Scientific and Precision Instruments, Laboratory Equipment (Including Supplies), Watches',
    EK: [{ commodityCode: '9711', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'INSTRUMTS,EQPT-DESIGNED FOR SCI SURGCL LAB,INDL RADIO OR TV',
    label: 'Instruments, Equipment - Designed for Scientific, Surgical, Laboratory',
    EK: [{ commodityCode: '9719', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'Military SAL Mail',
    label: 'Military SAL Mail',
    EK: [{ commodityCode: 'E0105', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
  {
    value: 'Military AirMail',
    label: 'Military AirMail',
    EK: [{ commodityCode: 'E0106', product: 'AXA', productSHC: 'AXA', commoditySHC: '' }],
  },
];
